<script setup lang="ts">
/**
 * MY — Change Officer 카테고리 단일 panel.
 *
 * 지원 task code:
 *   - M32105 Appointment of Director (TMP023~TMP026)
 *
 * 문서별 변수: preset_task/Appointment_of_Director_Variable_Map.md
 *   1) Directors' Circular Resolution (DCR)        — 필수
 *   2) Section 58B (New Director)                  — 필수
 *   3) Section 201 with attestation                — 선택 (외국인 / 공증 필요)
 *   4) Section 201 (plain, no attestation)         — 선택 (말레이시아 IC 보유자)
 *
 * 섹션:
 *   A. Company                  → 회사정보 (registration_no, constitution clause)
 *   B. New Director Basic Info  → 12개 필드 + Person Lookup AI
 *   C. Other Directorship       → 최대 4행 반복
 *   D. Dates                    → resolution_date, date_of_declaration
 *   E. Section 201 Variant      → attestation / plain 선택 (자동 추천 + override)
 */
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { Icon } from '@iconify/vue';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, OfficerRoleType } from '@/features/AI-documents/types/docGen.types';
import {
  buildAppointmentVariables,
  detectChangeOfficerDocFlags,
  type DirectorEntry,
  type DirectorHistoryRow,
  type GoverningDocumentType,
} from '../mappers/changeOfficerMapper';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { useDateField, useFormatDateByUserFormat, parseFlexibleDate } from '@/shared/composables/dateUtils';
import StructuredAddressInput from './StructuredAddressInput.vue';
import ContactRegisterModal from '@/features/startTask/preset_task/shared/preset/components/ContactRegisterModal.vue';
import racesData from '../../components/shared/data/common/races.json';
import gendersData from '../../components/shared/data/common/genders.json';
import nationalitiesData from '../../components/shared/data/common/nationalities.json';
import { OCCUPATIONS, resolveOccupation } from './cdd/cddOptions';

const raceOptions = racesData as Array<{ value: string; label: string }>;
const genderOptions = gendersData as Array<{ value: string; label: string }>;
const nationalityOptions = nationalitiesData as Array<{ value: string; label: string }>;
// Occupation 은 공용 cddOptions.OCCUPATIONS(케이스 토글 적용) 사용 — CDD/OTC 와 케이스 일관.
const occupationOptions = OCCUPATIONS;

/** v-combobox 가 객체 옵션({value,label})을 그대로 model 에 넣거나(Vuetify3 동작), 과거 잘못 저장된 '[object Object]' 를
 *  occupation 문자열로 정규화. 문자열이면 그대로, 객체면 label/value, '[object Object]' 면 빈 값. */
function occStr(v: any): string {
  let s = '';
  if (v == null) s = '';
  else if (typeof v === 'object') {
    s = String((v as any).label ?? (v as any).value ?? (v as any).title ?? (v as any).name ?? '').trim();
  } else {
    s = String(v).trim();
    if (s === '[object Object]') s = '';
  }
  return s ? resolveOccupation(s) : ''; // 공용 케이스 토글(cddOptions) 적용 — CDD/OTC 와 일관
}

interface PersonLookupContext {
  fieldGroup: 'newDirector';
  index?: number;
  defaultRole: OfficerRoleType | null;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: PersonLookupContext | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: 'newDirector'; index: number } | null;
  /** Edit 모드 — 값이 있으면 기존 task 수정(Update). 호스트가 restoreState 로 입력 복원. */
  editTaskId?: number | null;
}>();

// Edit(수정) 모드 — 버튼 라벨 'Update Task' + create→update 분기.
const editMode = computed(() => !!props.editTaskId);
// restoreState 직후 입력 스냅샷(기준선). Update Task 는 실제 변경이 있을 때만 활성화.
const editBaseline = ref<string | null>(null);
const isDirty = computed(() => {
  if (!editMode.value) return true;
  if (editBaseline.value == null) return false;
  try { return JSON.stringify(gatherState()) !== editBaseline.value; } catch { return true; }
});

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'create-task', data: { taskTitle: string }): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', context: PersonLookupContext): void;
  (
    e: 'request-address-lookup',
    context: { fieldGroup: 'newDirector'; index: number; addresses: any[]; subjectLabel?: string }
  ): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
}>();

const router = useRouter();

const creating = ref(false);
const progress = ref('');
// 진행 상태는 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다. (로컬 creating/progress 는 버튼 :loading·인라인 에러용으로 유지)
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
// L_TASK_CONFIG 의 processSteps (Doc / T-Sign / E-Filing / Reg) — task 생성 시 taskFlow 결정용.
const taskConfigSteps = ref<string[]>([]);
// Person Lookup 으로 선택한 contact 의 CD 보관 — Register 단계에서 사용 (TASK_DATA 에 저장).
//   직접 입력 경로면 비어있음 → Phase C 통합 모달에서 신규 Contact 등록 후 채워야 함.
const lookedUpContactCd = ref<string>('');

// ── Entity 정보 ──
const effectiveTaskTitle = computed(() => (props.taskTitle || '').trim());
const entityName = computed(() => {
  const name = props.entityData?.companyName || props.entityData?.entityName || '';
  const code = props.entityData?.companySuffix || '';
  const suffixMap: Record<string, string> = {
    M31001: 'SDN. BHD.',
    M31002: 'BHD.',
    M31003: 'LTD.',
    M31004: 'PTE. LTD.',
  };
  const suffix = suffixMap[code] || code;
  return suffix ? `${name} ${suffix}` : name;
});
const regNo = computed(() => props.entityData?.registrationNo || '');
const icpCd = computed(() => props.entityData?.icpCd || props.entityData?.entityCd || '');


// ── 문서 플래그 ──
const docFlags = computed(() => detectChangeOfficerDocFlags(props.selection.documents || []));

type DocMeta = { docCode: string; templateName: string };
const docsMeta = computed<DocMeta[]>(() =>
  (props.selection.documents || []).map((d: any) => ({
    docCode: String(d?.docCode || '').trim(),
    templateName: String(d?.templateName || d?.label || '').trim(),
  }))
);

function docLabel(d: DocMeta): string {
  return d.templateName || d.docCode || 'Document';
}

type SectionKey = 'company' | 'newDirector' | 'history' | 'dates';

function isDcr(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('circular resolution') || (n.includes('directors') && n.includes('resolution'));
}
function is58B(name: string): boolean {
  return /section\s*58\s*b/i.test(name);
}
function is201Attest(name: string): boolean {
  return /section\s*201/i.test(name) && /attest/i.test(name) && !/no\s*attest/i.test(name);
}
function is201Plain(name: string): boolean {
  return /section\s*201/i.test(name) && (/plain/i.test(name) || /no\s*attest/i.test(name) || !/attest/i.test(name));
}

function docsForSection(key: SectionKey): DocMeta[] {
  const all = docsMeta.value;
  switch (key) {
    case 'company':
      // company_name / regNo 는 1,2,3,4 공통
      return all;
    case 'newDirector':
      // 신규 이사 기본정보는 주로 58B + DCR/201 의 이름만 사용 — 모든 문서가 사용
      return all;
    case 'history':
      // Other Directorship — Section 58B 전용
      return all.filter((d) => is58B(d.templateName));
    case 'dates':
      // resolution_date(DCR) + date_of_declaration(201) — 두 그룹 합집합
      return all.filter(
        (d) => isDcr(d.templateName) || is201Attest(d.templateName) || is201Plain(d.templateName)
      );
    default:
      return all;
  }
}

const MAX_DOC_CHIPS = 3;

// ── 폼 필드 ──
// 회사
const constitutionClauseNo = ref('');
const articleNo = ref('');
const articleNoOptions = ref<string[]>([]);
const entityInfoLoading = ref(false);
// Entity 에서 로드된 Director article 원본 텍스트 — write-back 변경 감지 기준선.
const loadedArticleNo = ref('');
// Entity 에 정관/결의 근거가 등록돼 있으면 true → 라디오 + clause 잠금(변경 불가).
//   다른 직원이 정관번호를 잘못 선택하는 것을 방지. (Third Schedule 포함 모든 등록값 잠금)
const entityClauseRegistered = ref(false);
// Entity 기존 이사 목록 — Bulk Set Signers 풀용 (DCR 은 기존 board 가 서명).
const entityDirectorsList = ref<Array<{ name: string; email: string }>>([]);

function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

const constitutionClauseModel = computed<string>({
  get: () => constitutionClauseNo.value,
  set: (val: string | null) => {
    const s = val || '';
    constitutionClauseNo.value = parseClauseNo(s);
    // 선택한 옵션 전문 → governing document type 자동 감지. 필터(clauseOptionType)와 동일 분류기를
    //   써서 "Articles 탭에서 고른 항목이 Constitution 으로 튕기는" 불일치를 방지.
    const detected = clauseOptionType(s);
    if (detected !== 'other') governingDocumentType.value = detected;
  },
});

// DCR conditional 분기 — [[if constitution]] / [[if articles]] / [[if third_schedule]] 3택1.
//   기본값 constitution (신규 회사 일반). Third Schedule = Companies Act 2016 default (헌법 미채택).
const governingDocumentType = ref<GoverningDocumentType>('constitution');
const existingDirectorCount = ref<'single' | 'multiple'>('multiple');

/** Entity 등록 Governing Document 텍스트(articleNo/clauseNo) → 라디오 타입 + 파싱된 clause 번호 적용 + 잠금 플래그 set.
 *  watch(entity 로드) 와 restoreState(task 복원) 양쪽에서 동일하게 사용. */
function applyRegisteredClause(regClause: string): boolean {
  const s = String(regClause || '').trim();
  if (!s) return false;
  entityClauseRegistered.value = true;
  if (/third\s*schedule/i.test(s)) {
    governingDocumentType.value = 'third_schedule';
    constitutionClauseNo.value = '';
  } else if (/constitution/i.test(s)) {
    governingDocumentType.value = 'constitution';
    constitutionClauseNo.value = parseClauseNo(s);
  } else if (/articles?\s+of\s+association/i.test(s)) {
    governingDocumentType.value = 'articles';
    constitutionClauseNo.value = parseClauseNo(s);
  } else {
    governingDocumentType.value = 'constitution';
    constitutionClauseNo.value = parseClauseNo(s);
  }
  return true;
}

/** Governing Document 편집 잠금 — Entity 에 등록된 정관/결의 근거가 있으면 라디오 + clause readonly.
 *  articleNo 는 사용자가 직접 편집 불가(항상 entity 출처 — endpoint 또는 이전 로드 복원)라, fetch 타이밍/실패와 무관하게
 *  "entity 에 데이터가 존재" 의 신뢰 가능한 신호. (직원 오선택 방지) */
// 잠금(readonly)은 오직 Entity 등록 여부로만 결정한다 (new/edit 무관).
//   Entity 에 정관/결의 근거 미등록 → 항상 편집 가능. 저장된 task 의 articleNo 로는 잠그지 않음(restoreState 도 동일 정책).
//   entityClauseRegistered 는 icpCd watch(entity fetch)가 authoritative 로 설정.
const governingDocLocked = computed(() => entityClauseRegistered.value);

// Entity 변경 시 정관(clauseNo / articleNo) 자동 채움.
//   AGM 패널과 동일 엔드포인트 사용 — /api/setupEntity/changeAddress/address-info/:icpCd
watch(
  () => icpCd.value,
  async (code) => {
    if (!code) return;
    entityInfoLoading.value = true;
    try {
      const [addrJson, profileJson, directorsJson] = await Promise.all([
        fetch(`/api/setupEntity/changeAddress/address-info/${code}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
        // articleNoOptions 전용 경량 엔드포인트 (메모리 캐시) — 무거운 전체 프로필 대신.
        fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
        fetch(`/api/documents/variables/directors/${code}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
      ]);
      if (addrJson?.success) {
        articleNo.value = addrJson.articleNo || '';
        loadedArticleNo.value = String(addrJson.clauseNo || addrJson.articleNo || '').trim();
        // Governing Document(결의 근거) 를 Entity 의 등록 Director article/clause 텍스트로 자동 판정 + 잠금.
        //   ① Third Schedule → third_schedule, clause N/A  ② Articles of Association → articles  ③ 그 외 → constitution
        //   Entity 에 등록값이 있으면 잠금(entityClauseRegistered) — 직원 오선택 방지. edit/new 무관하게 entity 가 authoritative.
        // 우선순위: Third Schedule > Constitution > Articles. (applyRegisteredClause 내부)
        //   clauseNo 가 비어도 articleNo 로 fallback — 백엔드는 둘 다 ARTICLE_NO 전체 텍스트.
        const regClause = String(addrJson.clauseNo || addrJson.articleNo || '').trim();
        if (regClause) {
          applyRegisteredClause(regClause);
        } else {
          entityClauseRegistered.value = false;
        }
      }
      const opts =
        profileJson?.articleNoOptions ||
        profileJson?.data?.resolution?.articleNoOptions ||
        profileJson?.resolution?.articleNoOptions ||
        [];
      articleNoOptions.value = Array.isArray(opts)
        ? Array.from(new Set(opts.map((o: any) => String(o ?? '').trim()).filter(Boolean)))
        : [];
      // 기존 이사 수 자동 감지 — DCR [[if single_director_company]] / [[if multiple_directors_company]] 분기
      const existingDirs: any[] = directorsJson?.data?.directors || directorsJson?.directors || [];
      existingDirectorCount.value = existingDirs.length === 1 ? 'single' : 'multiple';
      // Bulk Set Signers 풀용 — 기존 이사 이름/이메일.
      entityDirectorsList.value = existingDirs
        .map((d: any) => ({
          name: String(d?.name || d?.contactName || d?.CONTACT_NAME || d?.fullName || '').trim(),
          email: String(d?.email || d?.contactEmail || d?.EMAIL || '').trim(),
        }))
        .filter((d) => d.name);
    } catch (err) {
      console.warn('[PreSetChangeOfficer] entity-info load failed:', err);
    } finally {
      entityInfoLoading.value = false;
    }
  },
  { immediate: true }
);

// Third Schedule 표준 옵션 — 백엔드 THIRD_SCHEDULE_OPTION 과 반드시 일치.
const THIRD_SCHEDULE_OPTION = 'Third Schedule (Companies Act 2016)';

/** 등록된 clause 옵션 문자열이 어떤 Governing Document 종류인지 분류. */
function clauseOptionType(opt: string): GoverningDocumentType | 'other' {
  const s = String(opt || '');
  // 우선순위: Third Schedule > Articles(Article(s)·구 Table A·Regulation·1965·LLP) > Constitution(Clause).
  //   "Article 92 of the Constitution (Articles of Association)" 는 Article 로 시작 → articles.
  //   filter 와 setter(clauseOptionType 위임)가 동일 분류 → 탭에 보인 항목 선택 시 라디오 튕김 없음.
  if (/third\s+schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s) || /\bllp\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}

/** 선택된 Governing Document 라디오에 맞는 clause 옵션만 노출.
 *   Constitution → Constitution 항목만, Articles → Article 항목만, Third Schedule → Third Schedule 항목만.
 *   분류 불가('other') 항목은 오분류로 숨겨지지 않도록 모든 모드에서 유지. */
const filteredClauseOptions = computed<string[]>(() => {
  const t = governingDocumentType.value;
  return articleNoOptions.value.filter((o) => {
    const ot = clauseOptionType(o);
    return ot === t || ot === 'other';
  });
});

/** 선택한 Governing Document → Entity Director article(L_ENTITY.ARTICLE_NO) 에 round-trip 가능한 텍스트로 변환. */
function buildGoverningDocumentText(): string {
  const clause = (constitutionClauseNo.value || '').trim();
  switch (governingDocumentType.value) {
    case 'third_schedule': return THIRD_SCHEDULE_OPTION;
    case 'articles': return clause ? `Article ${clause} of the Articles of Association` : 'Articles of Association';
    default: return clause ? `Clause ${clause} of the Constitution` : 'Constitution';
  }
}

// 신규 이사 기본정보
const newDirectorFullName = ref('');
const newDirectorNricOrPassport = ref('');
// 날짜 필드 — 세션 dateFormat 으로 표시되는 useDateField 패턴.
//   `*Field` — useDateField 반환값. dateValue(YYYY-MM-DD), dateDisplay(세션 포맷), datePickerValue(Date|null), dateMenu, datePlaceholder, datePickerProps.
//   `<원래변수>`= `*Field.dateValue` — 기존 .value 로 YYYY-MM-DD 접근하던 호환성 유지.
//   템플릿에서는 SG 패턴처럼 destructure 별칭으로 사용 (Vue 의 ref auto-unwrap 활용).
const newDirectorPassportExpiryDateField = useDateField();
const newDirectorPassportExpiryDate = newDirectorPassportExpiryDateField.dateValue;
const passportExpiryDateMenu = newDirectorPassportExpiryDateField.dateMenu;
const passportExpiryDateDisplay = newDirectorPassportExpiryDateField.dateDisplay;
const passportExpiryDatePickerValue = newDirectorPassportExpiryDateField.datePickerValue;
const passportExpiryDatePlaceholder = newDirectorPassportExpiryDateField.datePlaceholder;
const passportExpiryDatePickerProps = newDirectorPassportExpiryDateField.datePickerProps;
const passportExpiryLocalText = newDirectorPassportExpiryDateField.localText;
const passportExpiryOnBlur = newDirectorPassportExpiryDateField.onDateTextBlur;

const newDirectorDateOfBirthField = useDateField();
const newDirectorDateOfBirth = newDirectorDateOfBirthField.dateValue;
const dobMenu = newDirectorDateOfBirthField.dateMenu;
const dobDisplay = newDirectorDateOfBirthField.dateDisplay;
const dobPickerValue = newDirectorDateOfBirthField.datePickerValue;
const dobPlaceholder = newDirectorDateOfBirthField.datePlaceholder;
const dobPickerProps = newDirectorDateOfBirthField.datePickerProps;
const dobLocalText = newDirectorDateOfBirthField.localText;
const dobOnBlur = newDirectorDateOfBirthField.onDateTextBlur;
const newDirectorGender = ref<string>('');
const newDirectorRace = ref('');
// Nationality: 코드(B15xxx) 가 source of truth — 콤보박스 item-value.
//   라벨은 b15CodeMap 으로 코드로부터 도출 → 자동 동기화. 코드+코드명 페어 저장 패턴.
const newDirectorNationalityCd = ref('');
const newDirectorNationality = computed<string>(() => {
  const code = newDirectorNationalityCd.value;
  if (!code) return '';
  return b15CodeMap.value.get(code.toUpperCase()) || code;
});
const newDirectorOccupation = ref('');
const newDirectorEmail = ref('');
const newDirectorAppointmentDateField = useDateField();
const newDirectorAppointmentDate = newDirectorAppointmentDateField.dateValue;
const appointmentDateMenu = newDirectorAppointmentDateField.dateMenu;
const appointmentDateDisplay = newDirectorAppointmentDateField.dateDisplay;
const appointmentDatePickerValue = newDirectorAppointmentDateField.datePickerValue;
const appointmentDatePlaceholder = newDirectorAppointmentDateField.datePlaceholder;
const appointmentDatePickerProps = newDirectorAppointmentDateField.datePickerProps;
const appointmentLocalText = newDirectorAppointmentDateField.localText;
const appointmentOnBlur = newDirectorAppointmentDateField.onDateTextBlur;
// 주소 입력 — 단일 한 줄 string.
//   UI: StructuredAddressInput.vue (기본 textarea 표시, Edit 클릭 시 6필드 cascading dropdown).
//   Prefill: Contact Detail / Pick&Place 모두 formatAddrObjToString 으로 라벨 변환된 string 으로 set.
const residentialAddress = ref<string>('');
const serviceAddress = ref<string>('');

const newDirectorResidentialAddress = computed(() => (residentialAddress.value || '').trim());
const newDirectorServiceAddress = computed(() => (serviceAddress.value || '').trim());

// ── 추가 신규 이사 (Director 2+) — Secretary 패널과 동일한 배열+전체폼 패턴 ──
//   primary(Director 1) = 위 상세 폼 유지. 추가분도 동일한 전체 인적정보 폼.
//   각 director 는 Section 58B / 201 문서로 1장씩 복제 생성, DCR 반복부엔 전원 나열.
interface AdditionalDirectorRow {
  fullName: string; nricOrPassport: string; passportExpiryDate: string;
  dateOfBirth: string; gender: string; race: string;
  nationalityCd: string; nationality: string; businessOccupation: string;
  dateOfAppointment: string; residentialAddress: string; serviceAddress: string;
  email: string; contactCd: string;
  // Other Directorship History (Section 58B) — 디렉터별 보유. (_dateMenu = 행별 달력 팝업 상태)
  history: Array<{ no: string; company: string; date: string; _dateMenu?: boolean }>;
  historyMsg: string;
}
function createEmptyAdditionalDirector(): AdditionalDirectorRow {
  return {
    fullName: '', nricOrPassport: '', passportExpiryDate: '',
    dateOfBirth: '', gender: '', race: '', nationalityCd: '', nationality: '',
    businessOccupation: '', dateOfAppointment: '', residentialAddress: '',
    serviceAddress: '', email: '', contactCd: '',
    history: [{ no: '1', company: '', date: '' }],
    historyMsg: '',
  };
}
// Director 1 = index 0 (기본 1명). 모든 디렉터가 동일 카드 폼.
const additionalDirectors = ref<AdditionalDirectorRow[]>([createEmptyAdditionalDirector()]);
const nationalityLabels = nationalityOptions.map((o) => o.label);

// per-index 날짜 (text 버퍼 + 달력 menu) — Secretary 패널 패턴 (loop 안에서 useDateField 불가)
const addlDobMenu = ref<boolean[]>([false]);
const addlExpiryMenu = ref<boolean[]>([false]);
const addlAppointMenu = ref<boolean[]>([false]);
const addlDobText = ref<string[]>(['']);
const addlExpiryText = ref<string[]>(['']);
const addlAppointText = ref<string[]>(['']);
const fmtUserDate = useFormatDateByUserFormat();
function addlFmt(iso: string): string {
  if (!iso) return '';
  try { const d = new Date(iso); return isNaN(d.getTime()) ? iso : fmtUserDate(d); } catch { return iso; }
}
function addlParseIso(text: string): string {
  const t = (text || '').trim();
  if (!t) return '';
  const p = parseFlexibleDate(t);
  if (p) return p;
  try {
    const d = new Date(t);
    if (!isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { /* ignore */ }
  return '';
}
type AddlDateKey = 'dob' | 'expiry' | 'appoint';
const ADDL_DATE_FIELD: Record<AddlDateKey, 'dateOfBirth' | 'passportExpiryDate' | 'dateOfAppointment'> = {
  dob: 'dateOfBirth', expiry: 'passportExpiryDate', appoint: 'dateOfAppointment',
};
function addlTextRef(key: AddlDateKey) {
  return key === 'dob' ? addlDobText : key === 'expiry' ? addlExpiryText : addlAppointText;
}
function addlMenuRef(key: AddlDateKey) {
  return key === 'dob' ? addlDobMenu : key === 'expiry' ? addlExpiryMenu : addlAppointMenu;
}
function addlDateFromText(i: number, key: AddlDateKey, text: string) {
  const row = additionalDirectors.value[i]; if (!row) return;
  const field = ADDL_DATE_FIELD[key];
  if (!text.trim()) { row[field] = ''; return; }
  const iso = addlParseIso(text);
  if (iso) row[field] = iso; else addlTextRef(key).value[i] = addlFmt(row[field]);
}
function addlDateFromPicker(i: number, key: AddlDateKey, val: Date | null) {
  const row = additionalDirectors.value[i]; if (!row) return;
  const field = ADDL_DATE_FIELD[key];
  row[field] = val ? `${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}` : '';
  addlMenuRef(key).value[i] = false;
}
function addlPickerDate(iso: string): Date | null {
  if (!iso) return null;
  try { const d = new Date(iso); return isNaN(d.getTime()) ? null : d; } catch { return null; }
}
// row 날짜가 외부(✨ lookup)에서 바뀌면 text 버퍼 동기화
watch(additionalDirectors, (rows) => {
  rows.forEach((r, i) => {
    addlDobText.value[i] = addlFmt(r.dateOfBirth);
    addlExpiryText.value[i] = addlFmt(r.passportExpiryDate);
    addlAppointText.value[i] = addlFmt(r.dateOfAppointment);
  });
}, { deep: true });

function addDirector() {
  additionalDirectors.value.push(createEmptyAdditionalDirector());
  addlDobMenu.value.push(false); addlExpiryMenu.value.push(false); addlAppointMenu.value.push(false);
  addlDobText.value.push(''); addlExpiryText.value.push(''); addlAppointText.value.push('');
}
function removeDirector(i: number) {
  if (additionalDirectors.value.length <= 1) return; // Director 1 은 최소 유지
  additionalDirectors.value.splice(i, 1);
  addlDobMenu.value.splice(i, 1); addlExpiryMenu.value.splice(i, 1); addlAppointMenu.value.splice(i, 1);
  addlDobText.value.splice(i, 1); addlExpiryText.value.splice(i, 1); addlAppointText.value.splice(i, 1);
}

// ── 디렉터별 Other Directorship History (Section 58B) ──
function addDirHistoryRow(di: number) {
  const row = additionalDirectors.value[di]; if (!row) return;
  row.history.push({ no: String(row.history.length + 1), company: '', date: '' });
}
function removeDirHistoryRow(di: number, ri: number) {
  const row = additionalDirectors.value[di]; if (!row) return;
  row.history.splice(ri, 1);
  if (row.history.length === 0) row.history.push({ no: '1', company: '', date: '' });
  row.history.forEach((r, i) => (r.no = String(i + 1)));
}
async function autoFillDirHistory(di: number) {
  const row = additionalDirectors.value[di]; if (!row) return;
  const cc = String(row.contactCd || '').trim();
  if (!cc) {
    row.historyMsg = 'Please use the AI lookup (✨) on Full Name first to select a contact.';
    return;
  }
  row.historyMsg = 'Loading…';
  try {
    const { apiClient } = await import('@/shared/api/client');
    const res: any = await apiClient.get(`/Contact/${encodeURIComponent(cc)}/company-relations`);
    const list: any[] = res?.data?.companyRelations || res?.companyRelations || res?.data?.data?.companyRelations || [];
    const directorRels = list.filter((r) => {
      const roleRaw = String(r.role || r.roleType || r.ROLE_TYPE || r.role_type || r.Role || '').trim();
      if (!/^officer$/i.test(roleRaw)) return false;
      const posRaw = String(r.position || r.POSITION || r.positions || r.POSITIONS || r.Position || '').trim();
      return /director/i.test(posRaw);
    });
    if (directorRels.length === 0) { row.historyMsg = 'No Director relations found for this contact.'; return; }
    const existing = new Set(row.history.map((r) => r.company.trim().toUpperCase()).filter(Boolean));
    const merged = [
      ...row.history.filter((r) => r.company.trim()),
      ...directorRels
        .filter((r) => !existing.has(String(r.companyName || r.COMPANY_NAME || r.entityName || '').trim().toUpperCase()))
        .map((r) => ({
          no: '',
          company: String(r.companyName || r.COMPANY_NAME || r.entityName || '').trim(),
          date: normalizeIsoDate(r.appointmentDate || r.dateOfAppointment || r.APPOINTMENT_DATE || r.appointedDate || ''),
        })),
    ];
    if (merged.length === 0) merged.push({ no: '1', company: '', date: '' });
    merged.forEach((r, i) => (r.no = String(i + 1)));
    row.history = merged;
    row.historyMsg = `Filled ${directorRels.length} Director relation(s).`;
  } catch (err: any) {
    row.historyMsg = `Failed to load relations: ${err?.message || 'unknown error'}`;
  }
}
// Other Directorship 행 날짜 — 직접 입력(blur 파싱) + 달력 선택.
function normHistDate(di: number, hi: number) {
  const h = additionalDirectors.value[di]?.history?.[hi]; if (!h) return;
  const t = (h.date || '').trim(); if (!t) return;
  const iso = addlParseIso(t);
  if (iso) h.date = iso;
}
function setHistDate(di: number, hi: number, val: Date | null) {
  const h = additionalDirectors.value[di]?.history?.[hi]; if (!h) return;
  h.date = val ? `${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}` : '';
  h._dateMenu = false;
}

// 이력 (반복) — 기본 1행
const directorHistory = ref<DirectorHistoryRow[]>([{ no: '1', company: '', date: '' }]);
// Other Directorship 각 row 의 date picker — useDateField 패턴을 반복 row 에 인라인 구현.
//   row.date 는 항상 YYYY-MM-DD 로 저장, 표시는 세션 dateFormat 으로 포맷.
const directorHistoryDateMenu = ref<boolean[]>([false]);
const historyDateFormatter = useFormatDateByUserFormat();
function formatHistoryDate(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return historyDateFormatter(d);
  } catch {
    return iso;
  }
}
function parseHistoryDate(iso: string): Date | null {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}
function setHistoryDate(i: number, val: Date | null) {
  if (!directorHistory.value[i]) return;
  if (!val) {
    directorHistory.value[i].date = '';
  } else {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    directorHistory.value[i].date = `${y}-${m}-${d}`;
  }
  directorHistoryDateMenu.value[i] = false;
}
function setHistoryDateFromText(i: number, text: string) {
  if (!directorHistory.value[i]) return;
  const parsed = parseFlexibleDate(text);
  if (parsed) directorHistory.value[i].date = parsed;
}

// Other Directorship — Entity Lookup dialog.
//   AI 아이콘 클릭 시 panel 내 dialog 띄움 → entity 검색·선택 → row.company 에 set.
//   docGenApi.getEntities() = 동일 client 의 entity 들. 자유 입력은 combobox 가 기본 지원.
const entityCompanyOptions = ref<Array<{ name: string; regNo: string }>>([]);
(async () => {
  try {
    const { docGenApi } = await import('@/features/AI-documents/api/docGenApi');
    const res: any = await docGenApi.getEntities();
    const list: any[] = res?.data?.entities ?? (Array.isArray(res?.data) ? res.data : []);
    entityCompanyOptions.value = list
      .map((e: any) => ({
        name: e.companyName || e.COMPANY_NAME || e.entityName || '',
        regNo: e.regNo || e.REG_NO || e.registrationNo || '',
      }))
      .filter((e) => e.name);
  } catch {
    entityCompanyOptions.value = [];
  }
})();

// "Fill from Company Relations" 버튼 — Person Lookup 으로 선택한 contact 의 다른 회사
//   Director 이력을 GET /api/Contact/:cc/company-relations 로 가져와서 directorHistory 에 채움.
const relationsLoading = ref(false);
const relationsMessage = ref('');

// NRIC/Passport 로 client contact 검색 + 자동 contactCd 매칭.
//   한 번 호출 후 contactsCache 에 저장 (동일 entity 안에서 재사용).
const contactsCache = ref<any[] | null>(null);
async function loadContactsOnce(): Promise<any[]> {
  if (contactsCache.value) return contactsCache.value;
  try {
    const { docGenApi } = await import('@/features/AI-documents/api/docGenApi');
    const res: any = await docGenApi.getContacts(icpCd.value || '');
    const list: any[] = res?.data?.contacts || res?.contacts || [];
    contactsCache.value = list;
    return list;
  } catch {
    contactsCache.value = [];
    return [];
  }
}

/**
 * NRIC/Passport 값이 입력되면 contact list 에서 contactRegNo 일치하는 row 찾음.
 *   매칭되면 detail 호출 → panel 의 모든 필드 자동 prefill + Other Directorship 도 자동 채움.
 */
/**
 * NRIC/Passport 정규화 — dash/공백/슬래시/점 등 비교 무관 문자 제거 + upper-case.
 *   "920123-01-5758" → "920123015758"
 *   "920123 01 5758" → "920123015758"
 *   "A1234567B"      → "A1234567B"
 */
function normalizeNric(raw: any): string {
  return String(raw || '')
    .toUpperCase()
    .replace(/[\s\-/.()]/g, '');
}
async function lookupContactByNricAuto() {
  const nricNorm = normalizeNric(newDirectorNricOrPassport.value);
  if (!nricNorm) return;
  if (lookedUpContactCd.value) return; // 이미 매칭됨 — 중복 호출 방지
  const list = await loadContactsOnce();
  const match = list.find((c: any) => {
    const v = normalizeNric(
      c.contactRegNo || c.CONTACT_REG_NO || c.nricNo || c.passportNo || ''
    );
    return v && v === nricNorm;
  });
  if (!match) {
    relationsMessage.value = `No contact found for NRIC/Passport "${newDirectorNricOrPassport.value}".`;
    return;
  }
  const cc = String(match.contactCd || match.CONTACT_CD || '').trim();
  if (!cc) return;
  lookedUpContactCd.value = cc;

  // contact detail 가져와 prefill — Contact Detail 화면과 동일한 endpoint 사용
  //   GET /api/Contact/:cc → { contact: { FIRST_NAME, SURNAME, NATIONALITY_CD, DATE_OF_BIRTH, ... } }
  //   풀 detail (DocGen 변수용 detail 보다 풍부)
  try {
    const { apiClient } = await import('@/shared/api/client');
    const detailRes: any = await apiClient.get(`/Contact/${encodeURIComponent(cc)}`);
    const detail = detailRes?.data?.contact || detailRes?.contact || detailRes?.data || detailRes;
    if (detail) applyPersonDetailToForm(detail);
  } catch (err) {
    console.warn('[PreSetChangeOfficer] /api/Contact/:cc detail fetch failed:', err);
  }

  // Other Directorship 자동 채움
  await fillDirectorHistoryFromRelations();
}

/**
 * Contact detail (Person Lookup 또는 NRIC 자동 조회) → panel 필드 prefill.
 *   selectedPerson watcher 와 동일 로직 — 한 곳에 모음.
 */
// ── 공통 Contact 등록 모달 ──
//   '+ Register Contact' 클릭 → 모달에서 신규 contact 생성 → created 시 해당 director 행에 바인딩.
const contactRegisterOpen = ref(false);
let contactRegisterTargetIndex = -1;
function openContactRegister(index: number) {
  contactRegisterTargetIndex = index;
  contactRegisterOpen.value = true;
}
async function onContactRegistered(payload: { contactCd: string; type?: 'individual' | 'corporate'; data: any }) {
  const idx = contactRegisterTargetIndex;
  contactRegisterTargetIndex = -1;
  if (idx < 0 || !payload?.contactCd) return;

  // 신규 contact 로 이 행을 교체 — 기존(이전 contact/직접입력) 주소가 잔존하지 않도록 먼저 비운다.
  const target = additionalDirectors.value[idx];
  if (target) {
    target.residentialAddress = '';
    target.serviceAddress = '';
  }

  // Person Lookup 과 동일하게 getContactDetail 로 상세(주소 배열 포함)를 조회해 행에 바인딩.
  let person: any = { contactCd: payload.contactCd };
  try {
    const { docGenApi } = await import('@/features/AI-documents/api/docGenApi');
    const res: any = await docGenApi.getContactDetail(icpCd.value || '', payload.contactCd);
    const detail = res?.data || res;
    if (detail) {
      person = { ...detail, contactCd: payload.contactCd, addresses: Array.isArray(detail.addresses) ? detail.addresses : [] };
    }
  } catch (err) {
    console.warn('[PreSetChangeOfficer] getContactDetail after register failed:', err);
  }
  await applyPersonToAdditionalDirector(idx, person);

  // detail 로 주소가 안 채워졌으면(비어있을 때만) 모달 입력값(payload)으로 보강.
  if (target && !String(target.residentialAddress || '').trim()) {
    const payloadAddr = buildResidentialFromCreateData(payload.data);
    if (payloadAddr) target.residentialAddress = toTitleCaseAddress(payloadAddr);
  }
}

/** 모달 생성 입력값(CreateIndividualPayload)에서 residential 표시 문자열 조합. GUID/내부코드는 제외. */
function buildResidentialFromCreateData(d: any): string {
  if (!d) return '';
  const s = (x: any) => String(x ?? '').trim();
  const isGuid = (x: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(x);
  const isCode = (x: string) => /^B\d{2}/i.test(x); // B05/B15 등 basecode
  const keepName = (x: string) => x && !isGuid(x) && !isCode(x);
  if (d.addressType === 'B07002') {
    const f = d.foreignAddress || {};
    return [f.addressLine1, f.addressLine2, f.addressLine3].map(s).filter(Boolean).join(', ');
  }
  const l = d.localAddress || {};
  const lines = [l.addressLine1, l.addressLine2, l.addressLine3].map(s).filter(Boolean);
  const structured = lines.length ? [] : [
    [s(l.blockHouseNo), s(l.streetName)].filter(Boolean).join(' '),
    s(l.buildingEstateName),
    [s(l.level), s(l.unit)].filter(Boolean).join('-'),
  ].filter(Boolean);
  const postcode = s(l.post_code) || s(l.postalCode) || s(l.postcode);
  const tail = [s(l.city), s(l.state), postcode, s(l.country)].filter((x) => x && keepName(x) || x === postcode).filter(Boolean);
  return [...lines, ...structured, ...tail].filter(Boolean).join(', ');
}

/** 추가 디렉터(additionalDirectors[index]) 에 person lookup 결과를 전체 인적정보로 바인딩 (Director 1 과 동일 필드). */
async function applyPersonToAdditionalDirector(index: number, person: any) {
  const target = additionalDirectors.value[index];
  if (!target || !person) return;
  await ensureB15Cache();
  let src: any = person;
  const cc = String(person.contactCd || person.CONTACT_CD || '').trim();
  if (cc) {
    target.contactCd = cc;
    try {
      const { apiClient } = await import('@/shared/api/client');
      const detailRes: any = await apiClient.get(`/Contact/${encodeURIComponent(cc)}`);
      src = detailRes?.data?.contact || detailRes?.contact || detailRes?.data || detailRes || person;
    } catch (err) {
      console.warn('[PreSetChangeOfficer] additional director detail fetch failed:', err);
    }
  }
  const pick = (...keys: string[]): string => {
    for (const k of keys) { const v = src?.[k]; if (v != null && v !== '') return String(v); }
    return '';
  };
  let fullName = pick('fullName', 'contactName', 'name', 'CONTACT_NAME');
  if (!fullName) {
    const parts = [
      pick('firstName', 'FIRST_NAME'),
      pick('middleName', 'MIDDLE_NAME'),
      pick('surname', 'SURNAME', 'lastName', 'LAST_NAME'),
    ].filter(Boolean);
    if (parts.length) fullName = parts.join(' ');
  }
  if (fullName) target.fullName = fullName;
  const idNo = pick('nricNo', 'NRIC_NO', 'passportNo', 'PASSPORT_NO', 'identityNo', 'contactRegNo', 'CONTACT_REG_NO');
  if (idNo) target.nricOrPassport = idNo;
  const expiry = pick('passportExpiryDate', 'PASSPORT_NO_EXPIRY_DATE');
  if (expiry) target.passportExpiryDate = normalizeIsoDate(expiry);
  const dob = pick('dateOfBirth', 'DATE_OF_BIRTH');
  if (dob) target.dateOfBirth = normalizeIsoDate(dob);
  const gender = String(pick('gender', 'PROFILE_GENDER', 'GENDER')).toLowerCase();
  if (gender) target.gender = gender.startsWith('m') ? 'MALE' : gender.startsWith('f') ? 'FEMALE' : '';
  const raceVal = String(pick('race', 'PROFILE_RACE', 'RACE')).toUpperCase();
  if (raceVal) {
    const m = raceOptions.find((o) => o.value === raceVal || o.label.toUpperCase() === raceVal);
    target.race = m?.value || raceVal;
  }
  // getContactDetail 응답은 nationalityCd(camelCase, B15 코드)로 줌 — camelCase 키도 포함해야 함.
  const natRaw = pick('nationality', 'nationalityName', 'nationalityCd', 'NATIONALITY_CD', 'NATIONALITY_NAME');
  if (natRaw) {
    if (/^B15\d+/i.test(natRaw)) {
      target.nationalityCd = natRaw.toUpperCase();
      target.nationality = b15CodeMap.value.get(natRaw.toUpperCase()) || natRaw;
    } else {
      target.nationality = natRaw;
      void resolveNationalityBaseCode(natRaw).then((code) => { if (code) target.nationalityCd = code; });
    }
  }
  const occ = pick('occupation', 'businessOccupation', 'PROFILE_OCCUPATION', 'BUSINESS_OCCUPATION');
  if (occ) target.businessOccupation = occStr(occ);
  const emailVal = pick('email', 'emailAddress', 'EMAIL', 'EMAIL_ADDRESS', 'PROFILE_EMAIL', 'CONTACT_EMAIL');
  if (emailVal) target.email = emailVal.trim();
  // 주소 — selectedPerson.addresses(Title Case 정규화됨) 우선, 없으면 detail
  const addrs = Array.isArray((person as any).addresses) ? (person as any).addresses
    : (Array.isArray((src as any).addresses) ? (src as any).addresses : []);
  if (addrs.length > 0) {
    const res = addrs.find((a: any) => /resid/i.test(String(a?.type || a?.label || ''))) || addrs[0];
    const svc = addrs.find((a: any) => /serv|business/i.test(String(a?.type || a?.label || '')));
    if (res?.value) target.residentialAddress = toTitleCaseAddress(String(res.value));
    if (svc?.value) target.serviceAddress = toTitleCaseAddress(String(svc.value));
  }
}

async function applyPersonDetailToForm(person: any) {
  if (!person) return;
  // B15 캐시 준비 — country baseCode → 라벨 정규화에 필요.
  await ensureB15Cache();

  // 첫 번째 truthy 값을 키 후보 배열에서 반환 — camelCase + UPPER_CASE 둘 다 지원
  const pick = (...keys: string[]): any => {
    for (const k of keys) {
      const v = person[k];
      if (v !== null && v !== undefined && v !== '') return v;
    }
    return '';
  };

  const cc = pick('contactCd', 'contactCode', 'CONTACT_CD');
  if (cc) lookedUpContactCd.value = String(cc);

  // 이름 — Individual 은 FIRST_NAME + MIDDLE_NAME + SURNAME 조합, Corporate 은 CONTACT_NAME
  let fullName = pick('fullName', 'contactName', 'name', 'CONTACT_NAME');
  if (!fullName) {
    const first = pick('firstName', 'FIRST_NAME');
    const middle = pick('middleName', 'MIDDLE_NAME');
    const surname = pick('surname', 'SURNAME', 'lastName', 'LAST_NAME');
    const parts = [first, middle, surname].filter(Boolean);
    if (parts.length > 0) fullName = parts.join(' ');
  }
  if (fullName) newDirectorFullName.value = String(fullName);

  const idNo = pick(
    'nricNo', 'NRIC_NO',
    'passportNo', 'PASSPORT_NO',
    'identityNo',
    'contactRegNo', 'CONTACT_REG_NO'
  );
  if (idNo) newDirectorNricOrPassport.value = String(idNo);

  const passportExpiry = pick('passportExpiryDate', 'PASSPORT_NO_EXPIRY_DATE');
  if (passportExpiry) newDirectorPassportExpiryDate.value = normalizeIsoDate(passportExpiry);

  const dob = pick('dateOfBirth', 'DATE_OF_BIRTH');
  if (dob) newDirectorDateOfBirth.value = normalizeIsoDate(dob);

  const gender = String(pick('gender', 'PROFILE_GENDER', 'GENDER')).toLowerCase();
  if (gender) {
    newDirectorGender.value = gender.startsWith('m')
      ? 'MALE'
      : gender.startsWith('f')
        ? 'FEMALE'
        : '';
  }
  const raceVal = String(pick('race', 'PROFILE_RACE', 'RACE')).toUpperCase();
  if (raceVal) {
    const m = raceOptions.find((o) => o.value === raceVal || o.label.toUpperCase() === raceVal);
    newDirectorRace.value = m?.value || raceVal;
  }
  // Nationality — 응답이 baseCode('B15135') 또는 라벨('MALAYSIA') 일 수 있음.
  //   B15 baseCode 가 콤보박스의 source of truth — 코드를 직접 세팅.
  //   라벨로 들어오는 경우 resolveNationalityBaseCode 로 코드 역변환.
  const natRaw = String(pick(
    'nationality', 'nationalityName', 'NATIONALITY_CD', 'NATIONALITY_NAME'
  )).trim();
  if (natRaw) {
    if (/^B15\d+/i.test(natRaw)) {
      newDirectorNationalityCd.value = natRaw.toUpperCase();
    } else {
      void resolveNationalityBaseCode(natRaw).then((code) => {
        if (code) newDirectorNationalityCd.value = code;
      });
    }
  }
  const occ = pick(
    'occupation', 'businessOccupation', 'PROFILE_OCCUPATION', 'BUSINESS_OCCUPATION'
  );
  if (occ) newDirectorOccupation.value = occStr(occ);

  const emailVal = pick('email', 'emailAddress', 'EMAIL', 'EMAIL_ADDRESS', 'PROFILE_EMAIL', 'CONTACT_EMAIL');
  if (emailVal) newDirectorEmail.value = String(emailVal).trim();

  // 주소: 챗봇 카드와 동일 source (JSON_DATA street01..03) 우선.
  //   우선순위:
  //     1) RESIDENTIAL_ADDR_JSON_DATA / SERVICE_ADDR_JSON_DATA — raw JSON string → parse → formatAddrObjToString
  //        (street01..03 + post_code + city/state/country 풀 주소. 챗봇 카드와 동일 source)
  //     2) addresses[] 배열에서 type 매칭 (구버전 호환)
  //     3) residentialAddress / serviceAddress 등 raw string
  //     4) registeredAddress / alternateAddress 변환된 객체 (street 키 누락 가능 — 부분만)
  //   결과 string 의 baseCode (B15135 등) 는 b15 캐시로 라벨 정규화.
  const parseJsonField = (raw: any): any | null => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try { return JSON.parse(String(raw)); } catch { return null; }
  };
  const addressList: Array<{ type?: string; value?: string }> = Array.isArray((person as any).addresses)
    ? (person as any).addresses
    : [];
  const pickFromList = (...types: string[]): string => {
    for (const t of types) {
      const hit = addressList.find((a) => String(a.type || '').toLowerCase() === t);
      const v = hit ? String(hit.value || '').trim() : '';
      if (v) return v;
    }
    return '';
  };
  const normalizeBaseCodeInString = (s: string): string =>
    s.replace(/\bB\d{4,}\b/g, (m) => {
      const label = b15CodeMap.value.get(m.toUpperCase());
      return label || m;
    });

  if (!residentialAddress.value) {
    const jsonObj = parseJsonField(person.RESIDENTIAL_ADDR_JSON_DATA || person.residentialAddrJsonData);
    const fromJson = jsonObj ? formatAddrObjToString(jsonObj) : '';
    const fromList = pickFromList('residential');
    const resStr = pick('residentialAddress', 'homeAddress', 'primaryAddress');
    const resObj = person.registeredAddress || person.residential_address || null;
    const resObjStr = resObj ? formatAddrObjToString(resObj) : '';
    // 일부 케이스에서 fromList 가 street+postcode만 남는 문제가 있어 더 완전한 후보를 선택.
    let chosen = pickBestAddressLine(fromList, fromJson, (resStr ? String(resStr) : ''), resObjStr);
    chosen = toTitleCaseAddress(normalizeBaseCodeInString(chosen));
    if (chosen) residentialAddress.value = chosen;
  }
  if (!serviceAddress.value) {
    const jsonObj = parseJsonField(person.SERVICE_ADDR_JSON_DATA || person.serviceAddrJsonData);
    const fromJson = jsonObj ? formatAddrObjToString(jsonObj) : '';
    const fromList = pickFromList('service', 'business');
    const svcStr = pick('serviceAddress', 'businessAddress');
    const altObj = person.alternateAddress || null;
    // 챗봇 카드(addresses[]) 우선 — JSON_DATA(fromJson) 는 UUID 혼입 케이스가 있어 fallback.
    let chosen = fromList || fromJson || (svcStr ? String(svcStr) : '') || (altObj ? formatAddrObjToString(altObj) : '');
    chosen = toTitleCaseAddress(normalizeBaseCodeInString(chosen));
    if (chosen) serviceAddress.value = chosen;
  }
}

/**
 * Nationality / Country baseCode 변환 캐시 (B15).
 *   응답에 'B15135' 같은 baseCode 가 오면 라벨('MALAYSIA' 등) 로 변환 후 콤보 옵션 매칭.
 *   B15 외의 다른 값(이미 라벨)은 그대로 통과.
 */
const b15CodeMap = ref<Map<string, string>>(new Map());
const b15Options = ref<Array<{ code: string; name: string }>>([]);
let b15CacheLoaded = false;
async function ensureB15Cache(): Promise<void> {
  if (b15CacheLoaded) return;
  b15CacheLoaded = true;
  try {
    const { baseCodeApi } = await import('@/shared/api/baseCodeApi');
    const res: any = await baseCodeApi.getCodesByMainCodeOnly('B15');
    const items: any[] = res?.data?.codes || res?.codes || res?.data || [];
    const m = new Map<string, string>();
    const list: Array<{ code: string; name: string }> = [];
    for (const it of items) {
      const code = String(it.totalCode || it.TOTAL_CODE || it.subCode || it.SUB_CODE || '').trim();
      const name = String(it.codeName || it.CODE_NAME || '').trim();
      if (code && name) {
        m.set(code, name);
        list.push({ code, name });
      }
    }
    b15CodeMap.value = m;
    b15Options.value = list.sort((a, b) => a.name.localeCompare(b.name));
  } catch {
    /* ignore */
  }
}
// 패널 mount 시 즉시 캐시 로드 — Nationality 콤보박스 items 채우기 용.
void ensureB15Cache();
async function resolveNationalityCode(raw: string): Promise<string> {
  const s = String(raw || '').trim();
  if (!s) return '';
  // 이미 baseCode 형식이 아니면 그대로
  if (!/^B15\d+/i.test(s)) return s;
  await ensureB15Cache();
  return b15CodeMap.value.get(s.toUpperCase()) || s;
}

/** baseCode 형식이면 캐시 라벨 / 아니면 원본. (동기 lookup — 캐시 빌어있으면 원본 반환) */
function resolveCountryLabelSync(raw: any): string {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^B15\d+/i.test(s)) return b15CodeMap.value.get(s.toUpperCase()) || s;
  return s;
}

/**
 * 추가 director 행 Nationality 콤보박스 — 라벨 선택 시 B15 코드(nationalityCd)도 함께 저장.
 *   콤보박스는 라벨만 v-model 하므로 코드를 안 채우면 register 시 NATIONALITY_CD 누락("-") 발생.
 *   코드+코드명 항상 세트 저장 ([[feedback_code_with_name]] 규칙).
 */
async function onAddlDirectorNationalitySelect(row: AdditionalDirectorRow, v: any): Promise<void> {
  const label = String(v || '').trim();
  row.nationality = label;
  row.nationalityCd = label ? await resolveNationalityBaseCode(label) : '';
}

/**
 * 라벨 → B15 baseCode 역변환.
 *   Contact INSERT 시 NATIONALITY_CD 컬럼이 짧아서 라벨("KOREA_SOUTH" 등) 그대로 보내면 truncation 으로 실패.
 *   nationalities.json value 키(예: "MALAYSIA", "KOREA_SOUTH") 를 받아 B15 baseCode 로 변환한다.
 *   B15 캐시에 없으면 빈 문자열 — 백엔드 입장에서 nationality 미입력으로 처리.
 */
async function resolveNationalityBaseCode(raw: string): Promise<string> {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^B15\d+/i.test(s)) return s; // 이미 baseCode
  await ensureB15Cache();

  // 정규화 — 콤마/특수문자 제거, 토큰화.
  const norm = (x: string) =>
    String(x || '').toUpperCase().replace(/[,._\-/]+/g, ' ').replace(/\s+/g, ' ').trim();
  const tokens = (x: string) =>
    norm(x).split(' ').filter((t) => t.length >= 3); // 의미있는 토큰만 (3자 이상)

  // 후보 라벨: nationalities.json 의 value + label 모두 시도.
  const opt = nationalityOptions.find((o) => o.value.toUpperCase() === s.toUpperCase());
  const inputs = [s, opt?.label || '', opt?.value || ''];
  const candidateTokenSets = inputs.map(tokens).filter((t) => t.length > 0);
  const candidateNormalized = inputs.map(norm).filter(Boolean);

  // 1. 완전 일치 (정규화 후)
  for (const [code, name] of b15CodeMap.value.entries()) {
    const n = norm(name);
    if (candidateNormalized.includes(n)) return code;
  }
  // 2. 토큰 교집합 — 입력 토큰이 baseCode 이름에 모두 포함되거나 그 역
  //    예) input "SOUTH KOREA" vs basecode "KOREA, SOUTH" → 둘 다 KOREA + SOUTH 토큰 공유.
  for (const [code, name] of b15CodeMap.value.entries()) {
    const nameTokens = tokens(name);
    if (nameTokens.length === 0) continue;
    for (const cand of candidateTokenSets) {
      // 입력 토큰 전부가 baseCode 이름 토큰에 포함되거나, baseCode 이름 토큰 전부가 입력에 포함되는 경우.
      const inputInName = cand.every((t) => nameTokens.includes(t));
      const nameInInput = nameTokens.every((t) => cand.includes(t));
      if (inputInName || nameInInput) return code;
    }
  }
  // 3. 단일 토큰 substring 폴백 — 마지막 수단 (예: "KOREA" 만 입력해도 KOREA, SOUTH 매칭).
  for (const [code, name] of b15CodeMap.value.entries()) {
    const n = norm(name);
    for (const cand of candidateNormalized) {
      if (cand.length >= 4 && (n.includes(cand) || cand.includes(n))) return code;
    }
  }
  return '';
}

/** Contact Detail 의 주소 객체 → 한 줄 문자열.
 *   소스 키 패턴 (DB JSON_DATA 와 변환된 객체 모두 지원):
 *     1) JSON_DATA 원본: street01/street02/street03 + post_code + city + state + country
 *     2) registeredAddress / alternateAddress (변환됨): streetName/buildingName/blockNo + postalCode
 *   country 가 baseCode (B15135) 면 resolveCountryLabelSync 로 라벨 변환.
 *   소스 데이터 끝 콤마 ("BATU 9,") 는 chunk join 시 trim 으로 제거.
 */
/**
 * 주소 문자열을 Title Case 로 정규화 (각 단어 첫 글자만 대문자).
 * 백엔드 공통 포맷터(addressUtil.toTitleCase)와 동일한 표시 규칙 — 챗봇 카드와 일치시키기 위함.
 * 콤마/숫자/슬래시/하이픈 등 구조 문자는 보존하고 알파벳 토큰만 변환한다.
 */
function toTitleCaseAddress(str: string): string {
  if (!str) return '';
  return str.replace(/[A-Za-z]+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/** 주소 후보들 중 "가장 완전한" 문자열을 선택 (city/state/country 포함 선호). */
function pickBestAddressLine(...candidates: Array<any>): string {
  const list = candidates
    .map((c) => (c == null ? '' : String(c).trim()))
    .filter(Boolean);
  if (list.length === 0) return '';
  const score = (s: string): number => {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    const hasMalaysia = /\bmalaysia\b/i.test(s);
    return parts.length * 10 + (hasMalaysia ? 5 : 0) + Math.min(s.length, 200) / 50;
  };
  return list
    .map((s) => ({ s, sc: score(s) }))
    .sort((a, b) => b.sc - a.sc)[0].s;
}

function formatAddrObjToString(addr: any): string {
  if (!addr || typeof addr !== 'object') return '';
  // UUID 토큰(주소 레코드 ID 등) 은 주소 문자열에 새면 안 됨 → 개별 필드에서 제거.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const clean = (v: any): string => {
    const s = String(v == null ? '' : v).trim();
    return UUID_RE.test(s) ? '' : s;
  };
  const countryRaw = addr.country || addr.COUNTRY || addr.countryName || '';
  const country = resolveCountryLabelSync(countryRaw);
  const postCode = clean(addr.postalCode || addr.POSTAL_CODE || addr.post_code || addr.POST_CODE);
  const city = clean(addr.city || addr.CITY);
  const parts = [
    clean(addr.foreignAddressLine1 || addr.FOREIGN_ADDRESS_LINE_1),
    clean(addr.foreignAddressLine2 || addr.FOREIGN_ADDRESS_LINE_2),
    // JSON_DATA 원본 키 (chat 카드 source 와 동일).
    clean(addr.street01 || addr.STREET01),
    clean(addr.street02 || addr.STREET02),
    clean(addr.street03 || addr.STREET03),
    // 변환된 객체 키 (registeredAddress 등).
    clean(addr.streetName || addr.STREET_NAME),
    clean(addr.buildingName || addr.BUILDING_NAME),
    clean(addr.blockNo || addr.BLOCK_NO),
    clean(addr.addrLevel || addr.ADDR_LEVEL),
    clean(addr.addrUnit || addr.ADDR_UNIT),
    [postCode, city].filter(Boolean).join(' '),
    clean(addr.state || addr.STATE),
    country,
  ]
    // 각 part 의 trim + 끝 콤마/세미콜론/마침표 제거 (backend _cleanField 와 동일 정책).
    .map((s) => (s == null ? '' : String(s).trim().replace(/^[\s,.;]+|[\s,.;]+$/g, '').trim()))
    .filter(Boolean);
  return parts.join(', ');
}


async function fillDirectorHistoryFromRelations() {
  relationsMessage.value = '';
  let cc = lookedUpContactCd.value;

  // lookedUpContactCd 가 없으면 NRIC 필드로 contact 를 재매칭 (dash 제거 후 비교)
  if (!cc) {
    const nricNorm = normalizeNric(newDirectorNricOrPassport.value);
    if (!nricNorm) {
      relationsMessage.value =
        'Please use the AI lookup on Full Name first to select a contact, then click here.';
      return;
    }
    const list = await loadContactsOnce();
    const match = list.find((c: any) =>
      normalizeNric(c.contactRegNo || c.CONTACT_REG_NO || c.nricNo || c.passportNo || '') === nricNorm
    );
    if (!match) {
      relationsMessage.value = `No contact found matching NRIC/Passport "${newDirectorNricOrPassport.value}".`;
      return;
    }
    cc = String(match.contactCd || match.CONTACT_CD || '').trim();
    if (!cc) {
      relationsMessage.value = 'Contact found but has no contact code.';
      return;
    }
    lookedUpContactCd.value = cc;
  }

  relationsLoading.value = true;
  try {
    const { apiClient } = await import('@/shared/api/client');
    const res: any = await apiClient.get(`/Contact/${encodeURIComponent(cc)}/company-relations`);
    const list: any[] =
      res?.data?.companyRelations || res?.companyRelations || res?.data?.data?.companyRelations || [];
    // 대소문자 무관 비교 — 응답 키 형식 (role/Role/ROLE / position/POSITION) 변동에 안전.
    //   role 은 'Officer' 정확히 매칭 (/i),  position 은 'Director' 부분 매칭 (/i).
    const director = list.filter((r) => {
      const roleRaw = String(
        r.role || r.roleType || r.ROLE_TYPE || r.role_type || r.Role || ''
      ).trim();
      if (!/^officer$/i.test(roleRaw)) return false;
      const posRaw = String(
        r.position || r.POSITION || r.positions || r.POSITIONS || r.Position || ''
      ).trim();
      return /director/i.test(posRaw);
    });
    // 자기 자신 제외 안 함 — 모든 Officer 관계를 표에 set. 사용자가 X 로 직접 삭제.
    //   (이전에는 panel 의 entity 와 같은 companyCd 를 빼서 "유일한 이력 = 자기 자신" 케이스에 0건이 됐었음)
    const currentEntityCd = String(icpCd.value || '').trim();
    const filtered = director;
    if (filtered.length === 0) {
      relationsMessage.value = 'No Director relations found for this contact.';
      return;
    }
    // 기존 row 중 비어있지 않은 것만 보존 + 새 entity 들 append (중복 회사명 제외)
    // dedupe 도 대소문자 무관 (이름 표기 차이로 중복 행 방지)
    const existingCompanies = new Set(
      directorHistory.value
        .map((r) => r.company.trim().toUpperCase())
        .filter(Boolean)
    );
    const merged: DirectorHistoryRow[] = [
      ...directorHistory.value.filter((r) => r.company.trim()),
      ...filtered
        .filter((r) =>
          !existingCompanies.has(
            String(r.companyName || r.COMPANY_NAME || r.entityName || '').trim().toUpperCase()
          )
        )
        .map((r) => ({
          no: '',
          company: String(r.companyName || r.COMPANY_NAME || r.entityName || '').trim(),
          date: normalizeIsoDate(
            r.appointmentDate || r.dateOfAppointment || r.APPOINTMENT_DATE || r.appointedDate || ''
          ),
        })),
    ];
    if (merged.length === 0) merged.push({ no: '1', company: '', date: '' });
    // 행 번호 재정렬
    merged.forEach((r, i) => (r.no = String(i + 1)));
    directorHistory.value = merged;
    directorHistoryDateMenu.value = merged.map(() => false);
    relationsMessage.value = `Filled ${filtered.length} Director relation(s).`;
  } catch (err: any) {
    relationsMessage.value = `Failed to load relations: ${err?.message || 'unknown error'}`;
  } finally {
    relationsLoading.value = false;
  }
}
function addHistoryRow() {
  directorHistory.value.push({ no: String(directorHistory.value.length + 1), company: '', date: '' });
  directorHistoryDateMenu.value.push(false);
}
function removeHistoryRow(i: number) {
  directorHistory.value.splice(i, 1);
  directorHistoryDateMenu.value.splice(i, 1);
  directorHistory.value.forEach((r, idx) => (r.no = String(idx + 1)));
}

// 날짜
const resolutionDateField = useDateField();
const resolutionDate = resolutionDateField.dateValue;
const resolutionDateMenu = resolutionDateField.dateMenu;
const resolutionDateDisplay = resolutionDateField.dateDisplay;
const resolutionDatePickerValue = resolutionDateField.datePickerValue;
const resolutionDatePlaceholder = resolutionDateField.datePlaceholder;
const resolutionDatePickerProps = resolutionDateField.datePickerProps;
const resolutionLocalText = resolutionDateField.localText;
const resolutionOnBlur = resolutionDateField.onDateTextBlur;

const dateOfDeclarationField = useDateField();
const dateOfDeclaration = dateOfDeclarationField.dateValue;
const declarationDateMenu = dateOfDeclarationField.dateMenu;
const declarationDateDisplay = dateOfDeclarationField.dateDisplay;
const declarationDatePickerValue = dateOfDeclarationField.datePickerValue;
const declarationDatePlaceholder = dateOfDeclarationField.datePlaceholder;
const declarationDatePickerProps = dateOfDeclarationField.datePickerProps;
const declarationLocalText = dateOfDeclarationField.localText;
const declarationOnBlur = dateOfDeclarationField.onDateTextBlur;

// Nationality 기반 분기 — Passport Expiry / Residential Address 폼 분기에만 사용.
//   (Section 201 plain/attestation 자동 추천은 제거: 두 문서 모두 항상 생성 — IVY: "We need both doc")
const isMalaysian = computed(() => {
  const v = String(newDirectorNationality.value || '').trim();
  return /malaysi/i.test(v) || /^my$/i.test(v);
});

// 외국인 여부 — Nationality 기준 (≠ MALAYSIA / MY).
//   사용처:
//     - Passport Expiry Date: 외국인 전용 (숨김 + 필수 자동 전환). Malaysian (IC 보유) 은 만료 개념 없음.
//     - Residential Address: 항상 필수 (모든 이사). isForeigner 와 무관.
//     - Service Address: 항상 선택.
const isForeigner = computed(() => !isMalaysian.value);

// ── Person Lookup ──
function requestLookup(ctx: PersonLookupContext) {
  emit('request-person-lookup', ctx);
}

// ── Drag & Drop (챗봇 카드 → 폼 필드) ──
//   AGM 패턴 답습: 글로벌 dragstart/dragend 로 isDragging 추적,
//   각 입력 필드 위에 transparent overlay 를 겹쳐서 Vuetify 이벤트 가로채기를 우회.
const isDragging = ref(false);
function _onGlobalDragStart() {
  isDragging.value = true;
}
function _onGlobalDragEnd() {
  isDragging.value = false;
}
/**
 * 드롭된 임의 형식의 날짜 문자열을 type="date" input 이 받는 YYYY-MM-DD 로 정규화.
 *   "9 May 1975" / "1975-05-09" / "09/05/1975" / "1975.05.09" 등 모두 처리.
 *   파싱 실패 시 빈 문자열.
 */
function normalizeIsoDate(raw: any): string {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return '';
  // 이미 YYYY-MM-DD 형식이면 그대로
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function handleFieldDrop(event: DragEvent, setter: (val: string) => void) {
  isDragging.value = false;
  if (!event.dataTransfer) return;
  const json = event.dataTransfer.getData('application/x-entity-field');
  if (json) {
    try {
      const { value } = JSON.parse(json);
      if (value) {
        setter(String(value));
        return;
      }
    } catch {
      /* ignore */
    }
  }
  const text = event.dataTransfer.getData('text/plain');
  if (text) setter(String(text));
}
onMounted(async () => {
  document.addEventListener('dragstart', _onGlobalDragStart);
  document.addEventListener('dragend', _onGlobalDragEnd);
  // L_TASK_CONFIG.PROCESS_STEPS 로딩 — Document / T-Sign / E-Filing / Register 단계 결정.
  try {
    const taskCode = props.selection.task.TASK_CODE;
    if (taskCode) {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    }
  } catch (err) {
    console.warn('[PreSetChangeOfficer] task config load failed:', err);
  }
});
onUnmounted(() => {
  document.removeEventListener('dragstart', _onGlobalDragStart);
  document.removeEventListener('dragend', _onGlobalDragEnd);
});

watch(
  () => props.selectedPerson,
  async (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    if (!ctx || ctx.fieldGroup !== 'newDirector') return;

    // 추가 디렉터(인덱스 지정) → 경량 필드(이름/ID/국적)만 채우고 종료. primary 상세폼은 건드리지 않음.
    if (ctx.index != null && ctx.index >= 0) {
      await applyPersonToAdditionalDirector(ctx.index, person);
      return;
    }

    // chat widget 에서 넘어온 selectedPerson 은 contact list 의 minimal 데이터 (contactCd/name)
    //   → DOB/gender/race/nationality 등 채우려면 풀 detail 을 별도 fetch 해야 한다.
    //   NRIC blur lookup 과 동일하게 GET /api/Contact/:cc 사용.
    const cc = String((person as any).contactCd || (person as any).CONTACT_CD || '').trim();
    if (cc) {
      try {
        const { apiClient } = await import('@/shared/api/client');
        const detailRes: any = await apiClient.get(`/Contact/${encodeURIComponent(cc)}`);
        const detail =
          detailRes?.data?.contact ||
          detailRes?.contact ||
          detailRes?.data ||
          detailRes;
        if (detail && (detail.FIRST_NAME || detail.firstName || detail.CONTACT_NAME)) {
          // 주소는 selectedPerson.addresses(=챗봇 카드 source, addressResolver 로 Title Case 정규화됨) 가 정답.
          //   /Contact/:cc raw 응답은 street 에 UUID 가 섞여 있으므로, 카드와 동일하게 보이도록
          //   selectedPerson 의 formatted addresses[] 를 detail 에 병합해 prefill 이 이를 우선 사용하게 한다.
          if (
            Array.isArray((person as any).addresses) && (person as any).addresses.length > 0 &&
            (!Array.isArray((detail as any).addresses) || (detail as any).addresses.length === 0)
          ) {
            (detail as any).addresses = (person as any).addresses;
          }
          applyPersonDetailToForm(detail);
          await fillDirectorHistoryFromRelations();
          lookedUpContactCd.value = cc;
          return;
        }
      } catch (err) {
        console.warn('[PreSetChangeOfficer] selectedPerson detail fetch failed:', err);
        // 실패하면 minimal 데이터로라도 fallback.
      }
    }
    applyPersonDetailToForm(person);
  },
  { deep: true }
);

watch(
  () => props.selectedAddress,
  (addr) => {
    if (!addr || !addr.value) return;
    const ctx = props.activeAddressContext;
    if (!ctx || ctx.fieldGroup !== 'newDirector') return;
    const pos = String((addr.position || '').toLowerCase());
    // Pick&Place 로 선택된 주소 — string 으로 set + 포함된 baseCode (B15135 등) 라벨로 변환.
    const cleaned = toTitleCaseAddress(String(addr.value).replace(/\bB\d{4,}\b/g, (m) => b15CodeMap.value.get(m.toUpperCase()) || m));
    if (pos.includes('service') || pos.includes('business')) {
      serviceAddress.value = cleaned;
    } else {
      residentialAddress.value = cleaned;
    }
  },
  { deep: true }
);

// ── 유효성 검증 ──
const showValidationErrors = ref(false);
function isFilled(v: any): boolean {
  return v !== null && v !== undefined && String(v).trim() !== '';
}

// 사용자가 X 버튼으로 skip 한 필수 필드 — Date of Appointment 등을 blank 로 두고 lodge 직전 채우는 워크플로 지원.
const skippedFields = ref<Set<string>>(new Set());
function isFieldSkipped(key: string): boolean {
  return skippedFields.value.has(key);
}
function toggleFieldSkip(key: string, skipped: boolean) {
  const next = new Set(skippedFields.value);
  if (skipped) next.add(key);
  else next.delete(key);
  skippedFields.value = next;
}

function hasFieldError(key: string): boolean {
  if (!showValidationErrors.value) return false;
  if (isFieldSkipped(key)) return false;
  switch (key) {
    case 'newDirectorFullName':
      return !isFilled(newDirectorFullName.value);
    case 'newDirectorNricOrPassport':
      return !isFilled(newDirectorNricOrPassport.value);
    case 'newDirectorAppointmentDate':
      return !isFilled(newDirectorAppointmentDate.value);
    case 'newDirectorResidentialAddress':
      // 모든 이사 필수 (Section 58B). 단일 string — 빈값 체크.
      return !isFilled(residentialAddress.value);
    case 'newDirectorPassportExpiryDate':
      // 외국인 (Passport 소지) 일 때만 필수
      return isForeigner.value && !isFilled(newDirectorPassportExpiryDate.value);
    case 'resolutionDate':
      return docFlags.value.hasDcr && !isFilled(resolutionDate.value);
    case 'dateOfDeclaration':
      return docFlags.value.hasSection201Either && !isFilled(dateOfDeclaration.value);
    case 'constitutionClauseNo':
      // Third Schedule = 정관 없음 → clause N/A, 필수 아님.
      return docFlags.value.hasDcr
        && governingDocumentType.value !== 'third_schedule'
        && !isFilled(constitutionClauseNo.value);
    default:
      return false;
  }
}
// 이 panel 은 현재 문서별 skip 분기 없음 — 모든 문서 항상 생성.
//   (이전 Section 201 plain/attestation 강제 분기 제거됨. 두 문서 모두 필요한 케이스 지원.)
const docsToGenerate = computed(() => docsMeta.value);

const isFormValid = computed(() => {
  if (!props.entityData) return false;
  // 디렉터 배열 — 입력 시작한(이름/NRIC 중 하나라도) 디렉터만 검증, 최소 1명 완전 입력.
  const startedDirs = additionalDirectors.value.filter(
    (d) => isFilled(d.fullName) || isFilled(d.nricOrPassport)
  );
  if (startedDirs.length === 0) return false;
  for (const d of startedDirs) {
    if (!isFilled(d.fullName) || !isFilled(d.nricOrPassport)) return false;
  }
  if (docFlags.value.hasDcr) {
    if (!isFieldSkipped('resolutionDate') && !isFilled(resolutionDate.value)) return false;
    // Third Schedule = clause N/A → 필수 아님 (requiredField 와 동일).
    if (
      governingDocumentType.value !== 'third_schedule' &&
      !isFieldSkipped('constitutionClauseNo') &&
      !isFilled(constitutionClauseNo.value)
    ) return false;
  }
  if (
    docFlags.value.hasSection201Either &&
    !isFieldSkipped('dateOfDeclaration') &&
    !isFilled(dateOfDeclaration.value)
  ) return false;
  return true;
});

// ── Create Task ──
async function handleCreateTask() {
  const title = effectiveTaskTitle.value;
  if (!title) {
    progress.value = 'Error: Task Title is required — please enter it in the chat.';
    return;
  }
  if (!props.entityData) {
    progress.value = 'Error: Please select an entity in the chat first.';
    return;
  }
  if (!isFormValid.value) {
    showValidationErrors.value = true;
    progress.value = 'Error: Please fill all required fields.';
    return;
  }

  const taskTypeCd = props.selection.task.TASK_CODE;
  if (!taskTypeCd) {
    progress.value = 'Error: Task Code not configured in PreSet.';
    return;
  }

  creating.value = true;
  progress.value = 'Creating task...';

  try {
    // L_TASK_CONFIG.processSteps 로 단계 결정 (AGM 동일 패턴).
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    // Register 단계가 사용할 officerData payload — backend tasks.js 가 TASK_DATA.formData 로 저장.
    //   officerNameCd: Person Lookup 으로 선택한 contactCd. 직접 입력 경로면 빈 문자열 → Phase C 통합 모달이 채움.
    //   appointedDate: register API 의 appointmentDate 로 매핑됨 (store 의 extractValue 분기 참조).
    //   newDirector* 입력값 전부 동봉 — Phase C 모달의 prefill 및 Contact 신규 등록에 사용.
    // Nationality: 콤보박스 = B15 baseCode 직접 선택 → 코드/라벨 페어 저장.
    //   - newDirectorNationalityCd     — Contact INSERT 시 NATIONALITY_CD 컬럼용 baseCode (source)
    //   - newDirectorNationality       — UI 표시용 label (computed, baseCode 로부터 derived)
    //   ※ 코드+코드명은 항상 세트로 저장한다 (truncation/표시 안정성).
    // Skip 된 필드는 빈 문자열로 — DCR 변수(constitutionClauseNo / resolutionDate / dateOfDeclaration) blank lodge 보장.
    const valOrBlank = (key: string, v: string) => (isFieldSkipped(key) ? '' : v);

    // Register/Phase C 용 디렉터 배열 (TASK_DATA.formData.directors) — AOS 의 secretaries[] 와 동일 (탭 구분).
    const registerDirectors = additionalDirectors.value
      .filter((d) => (d.fullName || '').trim() || (d.nricOrPassport || '').trim())
      .map((d) => ({
        fullName: d.fullName,
        nricOrPassport: d.nricOrPassport,
        passportExpiryDate: d.passportExpiryDate,
        dateOfBirth: d.dateOfBirth,
        gender: d.gender,
        race: d.race,
        nationalityCd: d.nationalityCd,
        nationality: d.nationality,
        businessOccupation: occStr(d.businessOccupation),
        dateOfAppointment: d.dateOfAppointment,
        residentialAddress: d.residentialAddress,
        serviceAddress: d.serviceAddress,
        email: d.email,
        contactCd: d.contactCd,
      }));
    // 단일 register API / Phase C prefill 용 — 첫 디렉터를 top-level newDirector* 로.
    const primaryDir = registerDirectors[0] || null;

    const officerData: Record<string, any> = {
      officerNameCd: primaryDir?.contactCd || lookedUpContactCd.value || '',
      appointedDate: primaryDir?.dateOfAppointment || '',
      position: 'B30027', // Director
      newDirectorFullName: primaryDir?.fullName || '',
      newDirectorNricOrPassport: primaryDir?.nricOrPassport || '',
      newDirectorPassportExpiryDate: primaryDir?.passportExpiryDate || '',
      newDirectorDateOfBirth: primaryDir?.dateOfBirth || '',
      newDirectorGender: primaryDir?.gender || '',
      newDirectorRace: primaryDir?.race || '',
      newDirectorNationality: primaryDir?.nationality || '',
      newDirectorNationalityCd: primaryDir?.nationalityCd || '',
      newDirectorOccupation: primaryDir?.businessOccupation || '',
      newDirectorResidentialAddress: primaryDir?.residentialAddress || '',
      newDirectorServiceAddress: primaryDir?.serviceAddress || '',
      newDirectorEmail: primaryDir?.email || '',
      // 디렉터 배열 — Register Detail 다이얼로그가 탭으로 표시 (changeOfficer.ts register, repeatArrayPath:'directors').
      directors: registerDirectors,
      skippedFields: Array.from(skippedFields.value),
      // Edit 복원 스냅샷 — 호스트가 TASK_DATA.formData.presetForm 로 저장/복원.
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      // ── Update 분기 ──
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
      progress.value = `Updating documents (Task ${taskId})...`;
    } else {
      // ── Create 분기 ──
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          entityCd: icpCd.value || null,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: title,
          taskFlow,
          officerData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false;
        return;
      }
      progress.value = `Task created (ID: ${taskId}). Saving documents...`;
    }

    // 디렉터 배열(전체 인적정보) → DirectorEntry. 비어있는 행 제외. (Director 1 = index 0)
    const allDirectors: DirectorEntry[] = additionalDirectors.value
      .filter((d) => (d.fullName || '').trim() || (d.nricOrPassport || '').trim())
      .map((d): DirectorEntry => ({
        fullName: d.fullName,
        nricOrPassport: d.nricOrPassport,
        passportExpiryDate: d.passportExpiryDate,
        dateOfBirth: d.dateOfBirth,
        gender: d.gender,
        race: d.race,
        nationalityCd: d.nationalityCd,
        nationality: d.nationality,
        businessOccupation: occStr(d.businessOccupation),
        dateOfAppointment: d.dateOfAppointment,
        residentialAddress: d.residentialAddress,
        serviceAddress: d.serviceAddress,
        email: d.email,
        // 디렉터별 Other Directorship History (Section 58B). 비어있는 행 제외.
        history: (d.history || []).filter((h) => (h.company || '').trim() || (h.date || '').trim()),
      }));
    // 안전망: 최소 1명 (빈 디렉터) — blank lodge 허용.
    if (allDirectors.length === 0) allDirectors.push({ fullName: '', nricOrPassport: '' });

    const commonInputs = {
      companyName: entityName.value,
      registrationNo: regNo.value,
      constitutionClauseNo: valOrBlank('constitutionClauseNo', constitutionClauseNo.value),
      governingDocumentType: governingDocumentType.value,
      existingDirectorCount: existingDirectorCount.value,
      resolutionDate: valOrBlank('resolutionDate', resolutionDate.value),
      dateOfDeclaration: valOrBlank('dateOfDeclaration', dateOfDeclaration.value),
    };

    // DCR 등 공통 문서 — 디렉터 전원을 반복부([[if directors]])에 나열.
    const commonFormData = buildAppointmentVariables({ ...commonInputs, directors: allDirectors });

    // Bulk Set Signers 풀 — 기존 이사(DCR 서명) + 신규 임명 이사(58B/201 서명), 이름 기준 dedupe.
    const _seenDir = new Set<string>();
    const directorsPool = [
      ...entityDirectorsList.value,
      ...allDirectors.map((d) => ({ name: (d.fullName || '').trim(), email: (d.email || '').trim() })),
    ].filter((d) => {
      const k = d.name.toLowerCase();
      if (!d.name || _seenDir.has(k)) return false;
      _seenDir.add(k); return true;
    });
    const bulkPool = { __directorsRaw: directorsPool };

    // per-director 문서(Section 58B / Section 201) 는 디렉터별 1장 복제 (Secretary 패널과 동일).
    const isPerDirectorDoc = (name: string) => /section\s*58\s*b|section\s*201/i.test(name);
    const isDcr = (name: string) =>
      /circular\s*resolution/i.test(name) || (/directors/i.test(name) && /resolution/i.test(name));

    const docsToSave = (props.selection.documents || [])
      .filter((d) => d.tmpId)
      .flatMap((d) => {
        const name = String(d.templateName || '');
        if (isPerDirectorDoc(name) && !isDcr(name) && allDirectors.length > 0) {
          const baseLabel = name.replace(/\.docx?$/i, '');
          return allDirectors.map((dir, di) => {
            const dirName = (dir.fullName || '').trim();
            // per-director 문서(Section 58B/201)는 그 디렉터 본인이 서명자 → SIGNER_JSON 자동 저장.
            const dirSigners = dirName
              ? [{ name: dirName, email: (dir.email || '').trim(), role: 'signer' }]
              : undefined;
            return {
              templateId: d.tmpId,
              label: allDirectors.length > 1 ? `${baseLabel} — ${dir.fullName || `#${di + 1}`}` : d.templateName,
              personName: dirName || entityName.value || '',
              formData: { ...buildAppointmentVariables({ ...commonInputs, directors: [dir] }), ...bulkPool },
              entityCd: icpCd.value || undefined,
              signers: dirSigners,
            };
          });
        }
        return [{
          templateId: d.tmpId,
          label: d.templateName,
          personName: entityName.value || '',
          formData: { ...commonFormData, ...bulkPool },
          entityCd: icpCd.value || undefined,
        }];
      });

    if (docsToSave.length > 0) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) {
          progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
        }
      } catch (err: any) {
        console.warn('[PreSetChangeOfficer] Document save failed:', err);
      }
    }

    // (구) Governing Document write-back 제거: task 입력값을 L_ENTITY.ARTICLE_NO 에 저장하면
    //   같은 task edit 시 entity 가 "등록됨"으로 잠기는 부작용 → 자동 등록 안 함.
    //   잠금은 오직 genuine 등록(설립/소스시스템 ARTICLE_NO)일 때만. 같은 task 복원은 restoreState 가 처리.

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
    } catch {
      /* ignore */
    }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch {
      /* ignore */
    }

    try {
      progress.value = 'Preparing next screen...';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      const cacheKey = `presetTaskDetailPrefetch:${taskId}`;
      sessionStorage.setItem(cacheKey, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId, taskTitle: title });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    console.error('[PreSetChangeOfficer] create task failed:', err);
    progress.value = `Error: ${err.message}`;
  } finally {
    creating.value = false;
  }
}

// ─── Edit 지원: 전체 입력 스냅샷 gather/restore (호스트가 TASK_DATA.formData.presetForm 로 저장/복원) ───
function gatherState(): Record<string, any> {
  return {
    // 회사 / 정관
    constitutionClauseNo: constitutionClauseNo.value,
    articleNo: articleNo.value,
    governingDocumentType: governingDocumentType.value,
    existingDirectorCount: existingDirectorCount.value,
    // 신규 이사 기본정보
    newDirectorFullName: newDirectorFullName.value,
    newDirectorNricOrPassport: newDirectorNricOrPassport.value,
    newDirectorPassportExpiryDate: newDirectorPassportExpiryDate.value,
    newDirectorDateOfBirth: newDirectorDateOfBirth.value,
    newDirectorGender: newDirectorGender.value,
    newDirectorRace: newDirectorRace.value,
    newDirectorNationalityCd: newDirectorNationalityCd.value,
    newDirectorOccupation: newDirectorOccupation.value,
    newDirectorEmail: newDirectorEmail.value,
    newDirectorAppointmentDate: newDirectorAppointmentDate.value,
    residentialAddress: residentialAddress.value,
    serviceAddress: serviceAddress.value,
    // 추가 신규 이사 (DCR 반복부)
    additionalDirectors: JSON.parse(JSON.stringify(additionalDirectors.value || [])),
    // 이력 (반복)
    directorHistory: JSON.parse(JSON.stringify(directorHistory.value || [])),
    // Person Lookup contactCd (Register 단계 사용)
    lookedUpContactCd: lookedUpContactCd.value,
    // 날짜
    resolutionDate: resolutionDate.value,
    dateOfDeclaration: dateOfDeclaration.value,
    // skip 필드
    skippedFields: Array.from(skippedFields.value),
  };
}

function restoreState(s: Record<string, any> | null | undefined) {
  if (!s || typeof s !== 'object') return;
  // 잠금은 오직 Entity 등록 여부(entityClauseRegistered — icpCd watch 가 authoritative)로 결정한다.
  //   저장된 task 의 articleNo/clause 로는 잠그지 않는다: edit 진입 시 entity 미등록이면 편집 가능해야 함.
  //   entity 등록 시엔 watch 의 applyRegisteredClause 가 articleNo+잠금+값을 authoritative 로 덮으므로 여기선 건드리지 않는다.
  if (!entityClauseRegistered.value) {
    const restoredArticle = s.articleNo != null ? String(s.articleNo).trim() : '';
    if (restoredArticle) {
      // 과거 저장된 article/clause 전문 → 편집 가능한 clause 번호 + 타입으로 환원.
      constitutionClauseNo.value = parseClauseNo(restoredArticle);
      const t = clauseOptionType(restoredArticle);
      if (t !== 'other') governingDocumentType.value = t;
    } else {
      if (s.constitutionClauseNo != null) constitutionClauseNo.value = String(s.constitutionClauseNo);
      if (s.governingDocumentType != null) governingDocumentType.value = s.governingDocumentType;
    }
  }
  if (s.existingDirectorCount === 'single' || s.existingDirectorCount === 'multiple') {
    existingDirectorCount.value = s.existingDirectorCount;
  }
  if (s.newDirectorFullName != null) newDirectorFullName.value = String(s.newDirectorFullName);
  if (s.newDirectorNricOrPassport != null) newDirectorNricOrPassport.value = String(s.newDirectorNricOrPassport);
  if (s.newDirectorPassportExpiryDate != null) newDirectorPassportExpiryDate.value = String(s.newDirectorPassportExpiryDate);
  if (s.newDirectorDateOfBirth != null) newDirectorDateOfBirth.value = String(s.newDirectorDateOfBirth);
  if (s.newDirectorGender != null) newDirectorGender.value = String(s.newDirectorGender);
  if (s.newDirectorRace != null) newDirectorRace.value = String(s.newDirectorRace);
  if (s.newDirectorNationalityCd != null) newDirectorNationalityCd.value = String(s.newDirectorNationalityCd);
  if (s.newDirectorOccupation != null) newDirectorOccupation.value = occStr(s.newDirectorOccupation);
  if (s.newDirectorEmail != null) newDirectorEmail.value = String(s.newDirectorEmail);
  if (s.newDirectorAppointmentDate != null) newDirectorAppointmentDate.value = String(s.newDirectorAppointmentDate);
  if (s.residentialAddress != null) residentialAddress.value = String(s.residentialAddress);
  if (s.serviceAddress != null) serviceAddress.value = String(s.serviceAddress);
  if (Array.isArray(s.additionalDirectors) && s.additionalDirectors.length > 0) {
    additionalDirectors.value = s.additionalDirectors.map((d: any) => ({
      ...createEmptyAdditionalDirector(),
      ...d,
    }));
  } else if (s.newDirectorFullName || s.newDirectorNricOrPassport) {
    // 구 task 마이그레이션: primary 가 newDirector* 에 저장돼 있던 경우 → Director 1 카드로 이전.
    additionalDirectors.value = [{
      ...createEmptyAdditionalDirector(),
      fullName: String(s.newDirectorFullName || ''),
      nricOrPassport: String(s.newDirectorNricOrPassport || ''),
      passportExpiryDate: String(s.newDirectorPassportExpiryDate || ''),
      dateOfBirth: String(s.newDirectorDateOfBirth || ''),
      gender: String(s.newDirectorGender || ''),
      race: String(s.newDirectorRace || ''),
      nationalityCd: String(s.newDirectorNationalityCd || ''),
      businessOccupation: occStr(s.newDirectorOccupation),
      dateOfAppointment: String(s.newDirectorAppointmentDate || ''),
      residentialAddress: String(s.residentialAddress || ''),
      serviceAddress: String(s.serviceAddress || ''),
      email: String(s.newDirectorEmail || ''),
      history: Array.isArray(s.directorHistory) && s.directorHistory.length
        ? s.directorHistory.map((r: any, ri: number) => ({ no: String(r?.no || ri + 1), company: String(r?.company || ''), date: String(r?.date || '') }))
        : [{ no: '1', company: '', date: '' }],
    }];
  } else {
    additionalDirectors.value = [createEmptyAdditionalDirector()];
  }
  {
    const n = additionalDirectors.value.length;
    addlDobMenu.value = Array(n).fill(false);
    addlExpiryMenu.value = Array(n).fill(false);
    addlAppointMenu.value = Array(n).fill(false);
    addlDobText.value = additionalDirectors.value.map((r) => addlFmt(r.dateOfBirth));
    addlExpiryText.value = additionalDirectors.value.map((r) => addlFmt(r.passportExpiryDate));
    addlAppointText.value = additionalDirectors.value.map((r) => addlFmt(r.dateOfAppointment));
  }
  if (Array.isArray(s.directorHistory)) {
    directorHistory.value = s.directorHistory.length
      ? s.directorHistory.map((r: any, i: number) => ({
          no: String(r?.no || i + 1),
          company: String(r?.company || ''),
          date: String(r?.date || ''),
        }))
      : [{ no: '1', company: '', date: '' }];
    directorHistoryDateMenu.value = directorHistory.value.map(() => false);
  }
  if (s.lookedUpContactCd != null) lookedUpContactCd.value = String(s.lookedUpContactCd);
  if (s.resolutionDate != null) resolutionDate.value = String(s.resolutionDate);
  if (s.dateOfDeclaration != null) dateOfDeclaration.value = String(s.dateOfDeclaration);
  if (Array.isArray(s.skippedFields)) skippedFields.value = new Set(s.skippedFields);
  // 파생 watcher 가 정착한 뒤 기준선 캡처 → 이후 실제 변경만 dirty 로 감지.
  nextTick(() => { editBaseline.value = JSON.stringify(gatherState()); });
}

defineExpose({ gatherState, restoreState });
</script>

<template>
  <div class="co-panel">
    <!-- 헤더 -->
    <div class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Change Officer</div>
      </div>
      <v-btn variant="text" size="small" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>
        Back
      </v-btn>
    </div>

    <div class="co-content">
      <!-- Task Title (AGM 동일 디자인) -->
      <div class="co-section">
        <div class="co-section-header">
          <Icon icon="mdi:text-box-outline" width="16" color="#666" />
          <span>Task Title</span>
        </div>
        <div v-if="effectiveTaskTitle" class="d-flex align-start justify-space-between ga-3">
          <div class="co-readonly-box flex-grow-1">{{ effectiveTaskTitle }}</div>
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            class="co-change-btn"
            @click="emit('change-task-title')"
          >
            <v-icon start size="16">mdi-pencil</v-icon>
            Change
          </v-btn>
        </div>
        <div v-else class="co-readonly-box co-readonly-empty">
          Waiting for task title (enter it in the chat on the left)...
        </div>
      </div>

      <!-- Entity 카드 (AGM 동일 디자인) -->
      <div v-if="entityData" class="co-entity-card">
        <div class="d-flex align-center ga-3 justify-space-between">
          <div class="d-flex align-center ga-3">
            <div class="co-entity-icon">
              <Icon icon="mdi:domain" width="24" color="#1B84FF" />
            </div>
            <div>
              <div class="co-entity-name">{{ entityName }}</div>
              <div v-if="regNo" class="co-entity-reg">Registration No: {{ regNo }}</div>
              <div v-if="articleNo || constitutionClauseNo" class="co-entity-reg" :title="articleNo">
                Constitution Clause: <strong>{{ constitutionClauseNo || '—' }}</strong>
                <span v-if="articleNo" class="co-entity-article">({{ articleNo }})</span>
              </div>
            </div>
          </div>
          <v-btn
            variant="tonal"
            color="primary"
            size="small"
            class="co-change-btn"
            @click="emit('change-entity')"
          >
            <v-icon start size="16">mdi-swap-horizontal</v-icon>
            Change
          </v-btn>
        </div>
      </div>

      <div v-else class="co-empty">
        <Icon icon="mdi:link-variant-plus" width="40" color="#d0d0d0" class="mb-2" />
        <div style="font-size: 14px; font-weight: 600; color: #666;">No entity linked</div>
        <div style="font-size: 12px; color: #999; margin-top: 4px;">
          Use the chatbot on the left to select a T-Link entity.
        </div>
      </div>

      <!-- ── A. Company 섹션 ── -->
      <div v-if="entityData" class="co-section">
        <div class="co-section-header">
          <Icon icon="mdi:office-building" width="16" color="#888" />
          <span>Company</span>
          <div class="co-section-docs" v-if="docsForSection('company').length">
            <span class="co-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('company').slice(0, MAX_DOC_CHIPS)"
              :key="`co-c-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="co-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('company').length > MAX_DOC_CHIPS" class="co-more-chip">
              +{{ docsForSection('company').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col cols="6">
            <v-text-field
              :model-value="regNo"
              label="Registration No"
              variant="outlined"
              density="compact"
              hide-details
              readonly
            />
          </v-col>
          <v-col cols="6">
            <!-- Third Schedule: no constitution → clause N/A -->
            <v-text-field
              v-if="governingDocumentType === 'third_schedule'"
              model-value="N/A"
              disabled
              :label="governingDocLocked ? 'Constitution Clause No (registered)' : 'Constitution Clause No'"
              variant="outlined"
              density="compact"
              hide-details="auto"
            />
            <!-- Entity 에 등록된 정관/article → readonly 잠금 (직원 오선택 방지) -->
            <v-text-field
              v-else-if="governingDocLocked"
              v-model="constitutionClauseNo"
              :label="(governingDocumentType === 'articles' ? 'Article No' : 'Constitution Clause No') + ' (registered)'"
              readonly
              append-inner-icon="mdi-lock"
              variant="outlined"
              density="compact"
              hide-details="auto"
              persistent-hint
              hint="Registered in entity profile (locked)"
            />
            <!-- 미등록 → Constitution / Articles select or type clause/article number -->
            <v-combobox
              v-else
              v-model="constitutionClauseModel"
              :items="filteredClauseOptions"
              :label="(governingDocumentType === 'articles' ? 'Article No' : 'Constitution Clause No') + ((docFlags.hasDcr && !isFieldSkipped('constitutionClauseNo')) ? ' *' : '')"
              :placeholder="governingDocumentType === 'articles' ? 'Select or type article number' : 'Select or type clause number'"
              variant="outlined"
              density="compact"
              hide-details="auto"
              clearable
              :readonly="isFieldSkipped('constitutionClauseNo')"
              :class="{ 'co-field-skipped': isFieldSkipped('constitutionClauseNo') }"
              :error="hasFieldError('constitutionClauseNo')"
              :error-messages="hasFieldError('constitutionClauseNo') ? ['Required'] : []"
              :hint="
                filteredClauseOptions.length > 0
                  ? `${filteredClauseOptions.length} option(s) — only the number is stored`
                  : 'No options found — type manually'
              "
              persistent-hint
            >
              <template v-if="docFlags.hasDcr && !isFieldSkipped('constitutionClauseNo')" #prepend-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tipProps }">
                    <span v-bind="tipProps" class="co-skip-btn"
                      @mousedown.prevent
                      @click.stop="toggleFieldSkip('constitutionClauseNo', true)">
                      <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                    </span>
                  </template>
                  Skip this field (lodge as blank)
                </v-tooltip>
              </template>
              <template v-if="isFieldSkipped('constitutionClauseNo')" #append-inner>
                <a class="co-restore-link" @click.stop="toggleFieldSkip('constitutionClauseNo', false)">Restore</a>
              </template>
            </v-combobox>
          </v-col>
          <!-- Governing Document Type — DCR 의 [[if constitution]] / [[if articles]] / [[if third_schedule]] 분기 -->
          <v-col v-if="docFlags.hasDcr" cols="12">
            <div class="text-caption text-grey-darken-1 mb-1">
              Governing Document <span class="text-error">*</span>
              <span class="text-grey-darken-1 ml-2">— Resolution refers to this clause source</span>
              <v-icon v-if="governingDocLocked" size="12" class="ml-1">mdi-lock</v-icon>
              <span v-if="governingDocLocked" class="ml-1">(registered in entity — locked)</span>
            </div>
            <v-radio-group
              v-model="governingDocumentType"
              @update:model-value="constitutionClauseNo = ''"
              hide-details
              inline
              density="compact"
              class="mt-0"
              :disabled="governingDocLocked"
            >
              <v-radio label="Constitution" value="constitution" />
              <v-radio label="Articles of Association" value="articles" />
              <v-radio label="Third Schedule (Companies Act 2016)" value="third_schedule" />
            </v-radio-group>
          </v-col>
        </v-row>
      </div>

      <!-- ── B. New Director (Basic Info) ── -->
      <div v-if="entityData" class="co-section">
        <div class="co-section-header">
          <Icon icon="mdi:account-plus" width="16" color="#888" />
          <span>New Director</span>
          <div class="co-section-docs" v-if="docsForSection('newDirector').length">
            <span class="co-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('newDirector').slice(0, MAX_DOC_CHIPS)"
              :key="`co-nd-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="co-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('newDirector').length > MAX_DOC_CHIPS" class="co-more-chip">
              +{{ docsForSection('newDirector').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>

        <!-- (구) 단일 primary 폼 — Director 배열 카드로 대체되어 숨김. -->
        <v-row v-if="false" dense>
          <v-col cols="6">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="newDirectorFullName"
                :label="isFieldSkipped('newDirectorFullName') ? 'Full Name' : 'Full Name *'"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :readonly="isFieldSkipped('newDirectorFullName')"
                :class="{ 'co-field-skipped': isFieldSkipped('newDirectorFullName') }"
                :error="hasFieldError('newDirectorFullName')"
                :error-messages="hasFieldError('newDirectorFullName') ? ['Required'] : []"
              >
                <template v-if="!isFieldSkipped('newDirectorFullName')" #prepend-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tipProps }">
                      <span v-bind="tipProps" class="co-skip-btn"
                        @mousedown.prevent
                        @click.stop="toggleFieldSkip('newDirectorFullName', true)">
                        <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                      </span>
                    </template>
                    Skip this field (lodge as blank)
                  </v-tooltip>
                </template>
                <template #append-inner>
                  <a v-if="isFieldSkipped('newDirectorFullName')" class="co-restore-link"
                    @click.stop="toggleFieldSkip('newDirectorFullName', false)">Restore</a>
                  <v-tooltip v-else location="top" :open-delay="150">
                    <template #activator="{ props: tProps }">
                      <v-btn
                        v-bind="tProps"
                        icon="mdi-creation"
                        size="x-small"
                        variant="text"
                        color="primary"
                        @click="requestLookup({ fieldGroup: 'newDirector', defaultRole: null })"
                      />
                    </template>
                    <span>Lookup person from chat</span>
                  </v-tooltip>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => newDirectorFullName = v)" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="newDirectorNricOrPassport"
                :label="isFieldSkipped('newDirectorNricOrPassport') ? 'NRIC / Passport No' : 'NRIC / Passport No *'"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :readonly="isFieldSkipped('newDirectorNricOrPassport')"
                :class="{ 'co-field-skipped': isFieldSkipped('newDirectorNricOrPassport') }"
                :error="hasFieldError('newDirectorNricOrPassport')"
                :error-messages="hasFieldError('newDirectorNricOrPassport') ? ['Required'] : []"
                @blur="lookupContactByNricAuto"
              >
                <template v-if="!isFieldSkipped('newDirectorNricOrPassport')" #prepend-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tipProps }">
                      <span v-bind="tipProps" class="co-skip-btn"
                        @mousedown.prevent
                        @click.stop="toggleFieldSkip('newDirectorNricOrPassport', true)">
                        <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                      </span>
                    </template>
                    Skip this field (lodge as blank)
                  </v-tooltip>
                </template>
                <template v-if="isFieldSkipped('newDirectorNricOrPassport')" #append-inner>
                  <a class="co-restore-link" @click.stop="toggleFieldSkip('newDirectorNricOrPassport', false)">Restore</a>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => { newDirectorNricOrPassport = v; lookupContactByNricAuto(); })" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="dobLocalText"
                :placeholder="dobPlaceholder"
                label="Date of Birth"
                variant="outlined"
                density="compact"
                hide-details
                @blur="dobOnBlur($event.target.value)"
              >
                <template #append-inner>
                  <v-menu v-model="dobMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                    </template>
                    <v-date-picker
                      v-bind="dobPickerProps"
                      v-model="dobPickerValue"
                      @update:model-value="dobMenu = false"
                    />
                  </v-menu>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => newDirectorDateOfBirth = normalizeIsoDate(v))" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="co-drop-wrap">
              <v-autocomplete
                v-model="newDirectorGender"
                :items="genderOptions"
                item-title="label"
                item-value="value"
                label="Gender"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => newDirectorGender = v)" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="co-drop-wrap">
              <v-autocomplete
                v-model="newDirectorRace"
                :items="raceOptions"
                item-title="label"
                item-value="value"
                label="Race"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => newDirectorRace = v)" />
            </div>
          </v-col>
          <!-- Passport Expiry Date — 외국인(Passport 사용) 전용. Race 다음 위치. -->
          <v-col v-if="isForeigner" cols="6">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="passportExpiryLocalText"
                :placeholder="passportExpiryDatePlaceholder"
                :label="isFieldSkipped('newDirectorPassportExpiryDate') ? 'Passport Expiry Date' : 'Passport Expiry Date *'"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :class="{ 'co-field-skipped': isFieldSkipped('newDirectorPassportExpiryDate') }"
                :error="hasFieldError('newDirectorPassportExpiryDate')"
                :error-messages="hasFieldError('newDirectorPassportExpiryDate') ? ['Required'] : []"
                @blur="passportExpiryOnBlur($event.target.value)"
              >
                <template v-if="!isFieldSkipped('newDirectorPassportExpiryDate')" #prepend-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tipProps }">
                      <span v-bind="tipProps" class="co-skip-btn"
                        @mousedown.prevent
                        @click.stop="toggleFieldSkip('newDirectorPassportExpiryDate', true)">
                        <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                      </span>
                    </template>
                    Skip this field (lodge as blank)
                  </v-tooltip>
                </template>
                <template #append-inner>
                  <a v-if="isFieldSkipped('newDirectorPassportExpiryDate')" class="co-restore-link"
                    @click.stop="toggleFieldSkip('newDirectorPassportExpiryDate', false)">Restore</a>
                  <v-menu v-else v-model="passportExpiryDateMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                    </template>
                    <v-date-picker
                      v-bind="passportExpiryDatePickerProps"
                      v-model="passportExpiryDatePickerValue"
                      @update:model-value="passportExpiryDateMenu = false"
                    />
                  </v-menu>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => newDirectorPassportExpiryDate = normalizeIsoDate(v))" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="co-drop-wrap">
              <!-- Nationality: B15 baseCode 콤보박스. v-model = baseCode, 표시 = codeName. -->
              <v-autocomplete
                v-model="newDirectorNationalityCd"
                :items="b15Options"
                item-title="name"
                item-value="code"
                label="Nationality"
                placeholder="Select nationality"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => {
                  // 드롭된 값이 baseCode 이면 그대로, 라벨이면 비동기 변환.
                  const s = String(v || '').trim();
                  if (/^B15\d+/i.test(s)) newDirectorNationalityCd = s.toUpperCase();
                  else void resolveNationalityBaseCode(s).then((c) => { if (c) newDirectorNationalityCd = c; });
                })" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="co-drop-wrap">
              <v-autocomplete
                v-model="newDirectorOccupation"
                :items="occupationOptions"
                item-title="label"
                item-value="value"
                label="Business / Occupation"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => newDirectorOccupation = v)" />
            </div>
          </v-col>
          <v-col cols="6">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="appointmentLocalText"
                :placeholder="appointmentDatePlaceholder"
                :label="isFieldSkipped('newDirectorAppointmentDate') ? 'Date of Appointment' : 'Date of Appointment *'"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :class="{ 'co-field-skipped': isFieldSkipped('newDirectorAppointmentDate') }"
                :error="hasFieldError('newDirectorAppointmentDate')"
                :error-messages="hasFieldError('newDirectorAppointmentDate') ? ['Required'] : []"
                @blur="appointmentOnBlur($event.target.value)"
              >
                <template v-if="!isFieldSkipped('newDirectorAppointmentDate')" #prepend-inner>
                  <v-tooltip location="top" :open-delay="150">
                    <template #activator="{ props: tipProps }">
                      <span v-bind="tipProps" class="co-skip-btn"
                        @mousedown.prevent
                        @click.stop="toggleFieldSkip('newDirectorAppointmentDate', true)">
                        <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                      </span>
                    </template>
                    Skip — fill later before lodging (within 14 days)
                  </v-tooltip>
                </template>
                <template #append-inner>
                  <a v-if="isFieldSkipped('newDirectorAppointmentDate')" class="co-restore-link"
                    @click.stop="toggleFieldSkip('newDirectorAppointmentDate', false)">Restore</a>
                  <v-menu v-else v-model="appointmentDateMenu" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                    </template>
                    <v-date-picker
                      v-bind="appointmentDatePickerProps"
                      v-model="appointmentDatePickerValue"
                      @update:model-value="appointmentDateMenu = false"
                    />
                  </v-menu>
                </template>
              </v-text-field>
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => newDirectorAppointmentDate = normalizeIsoDate(v))" />
            </div>
          </v-col>
          <!-- Residential Address — 단일 textarea 표시, Edit 클릭 시 6필드 cascading dropdown -->
          <v-col cols="12">
            <div class="text-caption text-grey-darken-1 mb-1 d-flex align-center" style="gap: 6px;">
              <v-tooltip v-if="!isFieldSkipped('newDirectorResidentialAddress')" location="top" :open-delay="150">
                <template #activator="{ props: tipProps }">
                  <span v-bind="tipProps" class="co-skip-btn"
                    @click.stop="toggleFieldSkip('newDirectorResidentialAddress', true)">
                    <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                  </span>
                </template>
                Skip Residential Address (lodge as blank)
              </v-tooltip>
              <span>Residential Address</span>
              <span v-if="!isFieldSkipped('newDirectorResidentialAddress')" class="text-error">*</span>
              <span v-if="hasFieldError('newDirectorResidentialAddress')" class="text-error ml-2">
                Required
              </span>
              <a v-if="isFieldSkipped('newDirectorResidentialAddress')" class="co-restore-link ml-2"
                @click.stop="toggleFieldSkip('newDirectorResidentialAddress', false)">Restore</a>
            </div>
            <div :class="{ 'co-block-skipped': isFieldSkipped('newDirectorResidentialAddress') }">
              <StructuredAddressInput
                v-model="residentialAddress"
                label="Residential Address"
                :show-label="false"
                :dragging-active="isDragging && !isFieldSkipped('newDirectorResidentialAddress')"
                @drop-fill="(v) => (residentialAddress = v)"
              />
            </div>
          </v-col>

          <!-- Service Address — 단일 textarea 표시 + Edit 으로 structured 토글 (선택) -->
          <v-col cols="12">
            <div class="text-caption text-grey-darken-1 mb-1">Service Address (optional)</div>
            <StructuredAddressInput
              v-model="serviceAddress"
              label="Service Address"
              :show-label="false"
              :dragging-active="isDragging"
              @drop-fill="(v) => (serviceAddress = v)"
            />
          </v-col>

          <!-- Email Address — contact 선택 시 자동 셋팅, drag&drop 지원 -->
          <v-col cols="12">
            <div class="co-drop-wrap">
              <v-text-field
                v-model="newDirectorEmail"
                label="Email Address"
                type="email"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />
              <div v-if="isDragging" class="co-drop-overlay"
                @dragover.prevent
                @drop.prevent="handleFieldDrop($event, (v) => newDirectorEmail = v)" />
            </div>
          </v-col>
        </v-row>

        <!-- Directors — 모두 동일한 전체 폼 카드 (Director 1 = index 0) -->
        <div class="mt-3">
          <div v-for="(d, i) in additionalDirectors" :key="`add-dir-${i}`" class="co-addl-dir">
            <div class="d-flex align-center mb-2">
              <Icon icon="mdi:account-plus" width="16" color="#3b82f6" />
              <strong class="ml-1" style="font-size: 13px;">Director {{ i + 1 }}</strong>
              <v-spacer />
              <v-btn size="x-small" variant="tonal" color="primary" class="me-2" @click="openContactRegister(i)">
                <v-icon start size="14">mdi-account-plus-outline</v-icon>Register Contact
              </v-btn>
              <v-btn v-if="additionalDirectors.length > 1" size="x-small" variant="tonal" color="error" @click="removeDirector(i)">
                <v-icon start size="14">mdi-minus</v-icon>Remove
              </v-btn>
            </div>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="d.fullName" label="Full Name *" variant="outlined" density="compact" hide-details>
                  <template #append-inner>
                    <v-tooltip location="top" :open-delay="150">
                      <template #activator="{ props: tProps }">
                        <v-btn v-bind="tProps" icon="mdi-creation" size="x-small" variant="text" color="primary"
                          @click="requestLookup({ fieldGroup: 'newDirector', index: i, defaultRole: null })" />
                      </template>
                      <span>Lookup person from chat</span>
                    </v-tooltip>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="d.nricOrPassport" label="NRIC / Passport No *" variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="addlDobText[i]" label="Date of Birth" placeholder="DD/MM/YYYY"
                  variant="outlined" density="compact" hide-details
                  @blur="addlDateFromText(i, 'dob', addlDobText[i])">
                  <template #append-inner>
                    <v-menu v-model="addlDobMenu[i]" :close-on-content-click="false" location="bottom">
                      <template #activator="{ props: mp }">
                        <v-icon v-bind="mp" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                      </template>
                      <v-date-picker :model-value="addlPickerDate(d.dateOfBirth)"
                        @update:model-value="(v) => addlDateFromPicker(i, 'dob', v as Date)" />
                    </v-menu>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="d.gender" :items="genderOptions" item-title="label" item-value="value"
                  label="Gender" variant="outlined" density="compact" hide-details clearable />
              </v-col>
              <v-col cols="12" md="6">
                <v-select v-model="d.race" :items="raceOptions" item-title="label" item-value="value"
                  label="Race" variant="outlined" density="compact" hide-details clearable />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="addlExpiryText[i]" label="Passport Expiry Date" placeholder="DD/MM/YYYY"
                  variant="outlined" density="compact" hide-details
                  @blur="addlDateFromText(i, 'expiry', addlExpiryText[i])">
                  <template #append-inner>
                    <v-menu v-model="addlExpiryMenu[i]" :close-on-content-click="false" location="bottom">
                      <template #activator="{ props: mp }">
                        <v-icon v-bind="mp" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                      </template>
                      <v-date-picker :model-value="addlPickerDate(d.passportExpiryDate)"
                        @update:model-value="(v) => addlDateFromPicker(i, 'expiry', v as Date)" />
                    </v-menu>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-combobox :model-value="d.nationality" :items="nationalityLabels" label="Nationality"
                  variant="outlined" density="compact" hide-details auto-select-first clearable
                  @update:model-value="(v: any) => onAddlDirectorNationalitySelect(d, v)" />
              </v-col>
              <v-col cols="12" md="6">
                <v-combobox :model-value="d.businessOccupation" :items="occupationOptions" item-title="label" item-value="label"
                  label="Business / Occupation" variant="outlined" density="compact" hide-details clearable
                  @update:model-value="(v: any) => d.businessOccupation = occStr(v)" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="addlAppointText[i]" label="Date of Appointment" placeholder="DD/MM/YYYY"
                  variant="outlined" density="compact" hide-details
                  @blur="addlDateFromText(i, 'appoint', addlAppointText[i])">
                  <template #append-inner>
                    <v-menu v-model="addlAppointMenu[i]" :close-on-content-click="false" location="bottom">
                      <template #activator="{ props: mp }">
                        <v-icon v-bind="mp" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                      </template>
                      <v-date-picker :model-value="addlPickerDate(d.dateOfAppointment)"
                        @update:model-value="(v) => addlDateFromPicker(i, 'appoint', v as Date)" />
                    </v-menu>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="d.residentialAddress" label="Residential Address" rows="2" auto-grow
                  variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="d.serviceAddress" label="Service Address (optional)" rows="2" auto-grow
                  variant="outlined" density="compact" hide-details />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="d.email" label="Email Address" type="email"
                  variant="outlined" density="compact" hide-details clearable />
              </v-col>

              <!-- 디렉터별 Other Directorship History (Section 58B 문서 있을 때만) -->
              <v-col v-if="docFlags.hasSection58B" cols="12">
                <div class="addl-hist">
                  <div class="d-flex align-center mb-1">
                    <Icon icon="mdi:history" width="14" color="#888" />
                    <span class="ml-1 text-caption font-weight-medium">Other Directorship (Section 58B)</span>
                    <v-spacer />
                    <v-btn size="x-small" variant="text" color="primary" @click="autoFillDirHistory(i)">
                      <v-icon start size="14">mdi-auto-fix</v-icon>Auto Fill
                    </v-btn>
                    <v-btn size="x-small" variant="text" color="primary" @click="addDirHistoryRow(i)">
                      <v-icon start size="14">mdi-plus</v-icon>Add Row
                    </v-btn>
                  </div>
                  <div v-if="d.historyMsg" class="text-caption text-medium-emphasis mb-1">{{ d.historyMsg }}</div>
                  <v-row v-for="(h, hi) in d.history" :key="`h-${i}-${hi}`" dense align="center">
                    <v-col cols="2" md="1">
                      <v-text-field v-model="h.no" label="No" variant="outlined" density="compact" hide-details />
                    </v-col>
                    <v-col cols="10" md="7">
                      <v-text-field v-model="h.company" label="Company Name" variant="outlined" density="compact" hide-details />
                    </v-col>
                    <v-col cols="10" md="3">
                      <v-text-field v-model="h.date" label="Date" placeholder="YYYY-MM-DD"
                        variant="outlined" density="compact" hide-details
                        @blur="normHistDate(i, hi)">
                        <template #append-inner>
                          <v-menu v-model="h._dateMenu" :close-on-content-click="false" location="bottom">
                            <template #activator="{ props: mp }">
                              <v-icon v-bind="mp" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                            </template>
                            <v-date-picker :model-value="addlPickerDate(h.date)"
                              @update:model-value="(v) => setHistDate(i, hi, v as Date)" />
                          </v-menu>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="2" md="1" class="d-flex justify-end">
                      <v-btn icon="mdi-close" size="x-small" variant="text" color="error" @click="removeDirHistoryRow(i, hi)" />
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </div>
          <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-plus" class="mt-2" @click="addDirector">
            Add Director
          </v-btn>
        </div>
      </div>

      <!-- ── (구) 공유 Other Directorship — 디렉터별 카드 내부로 이동되어 숨김 ── -->
      <div
        v-if="false"
        class="co-section"
      >
        <div class="co-section-header">
          <Icon icon="mdi:history" width="16" color="#888" />
          <span>Other Directorship</span>
          <div class="co-section-docs">
            <span class="co-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('history').slice(0, MAX_DOC_CHIPS)"
              :key="`co-h-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="co-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
          </div>
          <v-tooltip location="top" :open-delay="150" class="ml-auto">
            <template #activator="{ props: tProps }">
              <v-btn
                v-bind="tProps"
                variant="text"
                size="x-small"
                color="primary"
                :loading="relationsLoading"
                :disabled="relationsLoading"
                @click="fillDirectorHistoryFromRelations"
              >
                <v-icon start size="14">mdi-creation</v-icon>
                Auto fill
              </v-btn>
            </template>
            <span>Lookup the selected contact's Director relations and fill the table</span>
          </v-tooltip>
          <v-btn
            variant="text"
            size="x-small"
            color="primary"
            @click="addHistoryRow"
          >
            <v-icon start size="14">mdi-plus</v-icon>
            Add Row
          </v-btn>
        </div>
        <div
          v-if="relationsMessage"
          class="text-caption text-grey-darken-1 px-3 pt-1"
        >
          {{ relationsMessage }}
        </div>

        <div
          v-for="(row, i) in directorHistory"
          :key="`history-${i}`"
          class="history-row"
        >
          <v-row dense>
            <v-col cols="1">
              <v-text-field
                v-model="row.no"
                label="No"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
            <v-col cols="7">
              <v-combobox
                v-model="row.company"
                :items="entityCompanyOptions"
                item-title="name"
                item-value="name"
                :return-object="false"
                label="Company Name"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              >
                <template #item="{ item, props: itemProps }">
                  <v-list-item v-bind="itemProps">
                    <v-list-item-subtitle v-if="item.raw.regNo" class="text-caption text-grey">
                      {{ item.raw.regNo }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="3">
              <v-text-field
                :model-value="formatHistoryDate(row.date)"
                label="Date"
                variant="outlined"
                density="compact"
                hide-details
                @change="(v: string) => setHistoryDateFromText(i, v)"
              >
                <template #append-inner>
                  <v-menu v-model="directorHistoryDateMenu[i]" :close-on-content-click="false" location="bottom">
                    <template #activator="{ props: menuProps }">
                      <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                    </template>
                    <v-date-picker
                      :model-value="parseHistoryDate(row.date)"
                      @update:model-value="(v) => setHistoryDate(i, v as Date | null)"
                    />
                  </v-menu>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="1" class="d-flex align-center justify-center">
              <v-btn
                variant="text"
                size="small"
                icon
                :disabled="directorHistory.length <= 1"
                @click="removeHistoryRow(i)"
              >
                <v-icon size="18" color="error">mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- ── D. Dates 섹션 ── -->
      <div v-if="entityData" class="co-section">
        <div class="co-section-header">
          <Icon icon="mdi:calendar-multiple" width="16" color="#888" />
          <span>Dates</span>
          <div class="co-section-docs" v-if="docsForSection('dates').length">
            <span class="co-section-docs-label">Applies to:</span>
            <v-chip
              v-for="d in docsForSection('dates').slice(0, MAX_DOC_CHIPS)"
              :key="`co-dt-${d.docCode || d.templateName}`"
              size="x-small"
              variant="tonal"
              color="primary"
              class="co-doc-chip"
            >
              {{ docLabel(d) }}
            </v-chip>
            <span v-if="docsForSection('dates').length > MAX_DOC_CHIPS" class="co-more-chip">
              +{{ docsForSection('dates').length - MAX_DOC_CHIPS }}
            </span>
          </div>
        </div>
        <v-row dense>
          <v-col v-if="docFlags.hasDcr" cols="6">
            <v-text-field
              v-model="resolutionLocalText"
              :placeholder="resolutionDatePlaceholder"
              :label="isFieldSkipped('resolutionDate') ? 'Resolution Date' : 'Resolution Date *'"
              variant="outlined"
              density="compact"
              hide-details="auto"
              :class="{ 'co-field-skipped': isFieldSkipped('resolutionDate') }"
              :error="hasFieldError('resolutionDate')"
              :error-messages="hasFieldError('resolutionDate') ? ['Required'] : []"
              @blur="resolutionOnBlur($event.target.value)"
            >
              <template v-if="!isFieldSkipped('resolutionDate')" #prepend-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tipProps }">
                    <span v-bind="tipProps" class="co-skip-btn"
                      @mousedown.prevent
                      @click.stop="toggleFieldSkip('resolutionDate', true)">
                      <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                    </span>
                  </template>
                  Skip — fill before lodging
                </v-tooltip>
              </template>
              <template #append-inner>
                <a v-if="isFieldSkipped('resolutionDate')" class="co-restore-link"
                  @click.stop="toggleFieldSkip('resolutionDate', false)">Restore</a>
                <v-menu v-else v-model="resolutionDateMenu" :close-on-content-click="false" location="bottom">
                  <template #activator="{ props: menuProps }">
                    <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                  </template>
                  <v-date-picker
                    v-bind="resolutionDatePickerProps"
                    v-model="resolutionDatePickerValue"
                    @update:model-value="resolutionDateMenu = false"
                  />
                </v-menu>
              </template>
            </v-text-field>
            <div class="text-caption text-grey-darken-1 mt-1">For DCR</div>
          </v-col>
          <v-col v-if="docFlags.hasSection201Either" cols="6">
            <v-text-field
              v-model="declarationLocalText"
              :placeholder="declarationDatePlaceholder"
              :label="isFieldSkipped('dateOfDeclaration') ? 'Date of Declaration' : 'Date of Declaration *'"
              variant="outlined"
              density="compact"
              hide-details="auto"
              :class="{ 'co-field-skipped': isFieldSkipped('dateOfDeclaration') }"
              :error="hasFieldError('dateOfDeclaration')"
              :error-messages="hasFieldError('dateOfDeclaration') ? ['Required'] : []"
              @blur="declarationOnBlur($event.target.value)"
            >
              <template v-if="!isFieldSkipped('dateOfDeclaration')" #prepend-inner>
                <v-tooltip location="top" :open-delay="150">
                  <template #activator="{ props: tipProps }">
                    <span v-bind="tipProps" class="co-skip-btn"
                      @mousedown.prevent
                      @click.stop="toggleFieldSkip('dateOfDeclaration', true)">
                      <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
                    </span>
                  </template>
                  Skip — fill before lodging
                </v-tooltip>
              </template>
              <template #append-inner>
                <a v-if="isFieldSkipped('dateOfDeclaration')" class="co-restore-link"
                  @click.stop="toggleFieldSkip('dateOfDeclaration', false)">Restore</a>
                <v-menu v-else v-model="declarationDateMenu" :close-on-content-click="false" location="bottom">
                  <template #activator="{ props: menuProps }">
                    <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
                  </template>
                  <v-date-picker
                    v-bind="declarationDatePickerProps"
                    v-model="declarationDatePickerValue"
                    @update:model-value="declarationDateMenu = false"
                  />
                </v-menu>
              </template>
            </v-text-field>
            <div class="text-caption text-grey-darken-1 mt-1">For Section 201</div>
          </v-col>
        </v-row>
      </div>

      <!-- ── Documents to be generated ── -->
      <div v-if="entityData" class="co-section">
        <div class="co-section-header">
          <Icon icon="mdi:file-document-multiple-outline" width="16" color="#888" />
          <span>Documents to be generated</span>
          <v-chip size="x-small" variant="tonal" color="primary" class="ml-2">
            {{ docsToGenerate.length }} / {{ selection.documents.length }}
          </v-chip>
        </div>
        <v-list density="compact" class="pa-0">
          <v-list-item
            v-for="(d, i) in docsMeta"
            :key="`gen-${i}-${d.docCode || d.templateName}`"
            class="px-3 py-1 doc-gen-row"
          >
            <template #prepend>
              <v-icon size="18" color="success">mdi-check-circle-outline</v-icon>
            </template>
            <v-list-item-title class="text-body-2">
              {{ docLabel(d) }}
            </v-list-item-title>
            <template #append>
              <v-chip size="x-small" variant="tonal" color="success">
                Will generate
              </v-chip>
            </template>
          </v-list-item>
        </v-list>
      </div>

      <div v-if="progress" class="text-caption mt-2" :class="progress.startsWith('Error:') ? 'text-error' : 'text-grey-darken-1'">{{ progress }}</div>
    </div>

    <!-- AGM 동일 패턴 — 하단 footer 의 Back / Create Task -->
    <div class="co-footer">
      <v-btn variant="outlined" color="grey" size="small" @click="emit('back')">
        <Icon icon="mdi:arrow-left" width="16" class="mr-1" />
        Back
      </v-btn>
      <v-tooltip :disabled="!(editMode && !isDirty)" location="top" text="No changes to update">
        <template #activator="{ props: tProps }">
          <span v-bind="tProps">
            <v-btn
              variant="flat"
              color="primary"
              size="small"
              :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
              :disabled="creating || (editMode && !isDirty)"
              :loading="creating"
              @click="handleCreateTask"
            >
              {{ editMode ? 'Update Task' : 'Create Task' }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </div>

    <!-- 공통 Contact 등록 모달 — 직접입력 대신 신규 contact 생성 후 행 자동 채움. -->
    <ContactRegisterModal v-model="contactRegisterOpen" @created="onContactRegistered" />
  </div>
</template>

<style scoped>
/* AGM 패턴 답습 — panel / header / content */
.co-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.co-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.co-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
}
.co-subtitle {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}
.co-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

/* AGM Entity 카드 / Task Title 박스 */
.co-entity-card {
  background: #f0f5ff;
  border: 1px solid #d6e4ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}
.co-entity-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.co-entity-name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}
.co-entity-reg {
  font-size: 12px;
  color: #666;
  margin-top: 1px;
}
.co-entity-article {
  color: #999;
  margin-left: 4px;
  font-size: 11px;
  display: inline-block;
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.co-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 16px;
  background: #fafafa;
  border: 1px dashed #e0e0e0;
  border-radius: 12px;
  margin-bottom: 20px;
  text-align: center;
}
.co-readonly-box {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  color: #333;
  line-height: 1.5;
}
.co-readonly-empty {
  color: #bbb;
  font-style: italic;
}
.co-change-btn {
  align-self: flex-start;
}

/* AGM 패턴 답습 — 섹션 카드 */
.co-section {
  margin-bottom: 16px;
  background: #ffffff;
  border: 1px solid #e9edf5;
  border-radius: 12px;
  padding: 0 0 12px 0;
  overflow: hidden;
}
.co-section > :not(.co-section-header) {
  padding-left: 14px;
  padding-right: 14px;
}
.co-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #283593;
  margin: 0 0 12px 0;
  padding: 10px 14px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-bottom: 1px solid #d6dcf2;
}
.co-section-docs {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  flex-wrap: wrap;
}
.co-section-docs-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
}
.co-doc-chip {
  max-width: 240px;
}
.co-doc-chip :deep(.v-chip__content) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.co-more-chip {
  font-size: 11px;
  color: #888;
}

.history-row {
  padding: 6px 0;
  border-bottom: 1px dashed #eef2ff;
}
.history-row:last-child {
  border-bottom: none;
}

/* AGM 패턴 답습 — 하단 footer */
.co-footer {
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
  border-top: 1px solid #f0f0f0;
}

/* AGM 패턴 답습 — drag&drop overlay */
.co-addl-dir {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  background: rgba(var(--v-theme-primary), 0.02);
}
.addl-hist {
  border-top: 1px dashed rgba(var(--v-theme-on-surface), 0.12);
  padding-top: 8px;
  margin-top: 4px;
}
.co-drop-wrap {
  position: relative;
}
.co-drop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  border: 2px dashed #5c6bc0;
  border-radius: 6px;
  background: rgba(92, 107, 192, 0.05);
  cursor: copy;
  transition: background 0.15s ease;
}
.co-drop-overlay:hover {
  background: rgba(92, 107, 192, 0.18);
  border-color: #3949ab;
}

/* Skip 버튼 — 필수 필드 X 표시. T-Doc AI VariableFormFields.vue 의 .skip-btn 과 동일 패턴. */
.co-skip-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s ease;
  margin-right: 2px;
}
.co-skip-btn:hover {
  background: rgba(244, 67, 54, 0.12);
}
.co-skip-btn:hover :deep(.v-icon) {
  color: #f44336 !important;
}
.co-restore-link {
  font-size: 12px;
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
}
.co-restore-link:hover {
  color: #0d47a1;
}
/* Skip 된 입력 — 회색톤으로 표시 (값은 유지되지만 비활성화). */
.co-field-skipped :deep(.v-field) {
  background-color: #f5f5f5;
}
.co-field-skipped :deep(.v-label) {
  text-decoration: line-through;
  opacity: 0.6;
}
.co-block-skipped {
  opacity: 0.55;
  filter: grayscale(0.4);
}
</style>
